<template>
  <form ref="formRef">
    <!-- Full-screen "Uploading receipt…" overlay while a UPLOAD NEW RECEIPT upload
         is in flight (covers Legacy upload + Photon scan + prefill). Matches the
         mobile.vue FAB-flow overlay visually so users get a consistent loading
         affordance whether they upload from the FAB or from the Reconcile screen.
         Driven by the local uploadingScanOverlay flag, toggled in uploadNewReceipt
         and the scanCompleted watcher. Mobile-only (isMobile prop): desktop keeps
         its pre-existing inline receipt-upload-progress bar instead. -->
    <div v-if="isMobile && uploadingScanOverlay" class="receipt-scan-overlay">
      <div class="receipt-scan-overlay-card">
        <i class="fas fa-spinner fa-spin receipt-scan-overlay-spinner"></i>
        <div class="receipt-scan-overlay-title">Uploading receipt…</div>
        <div class="receipt-scan-overlay-subtitle">Just a moment.</div>
      </div>
    </div>
    <div class='reconcile-transaction-overlay col-md-12'>
      <div class="overview col-md-12 no-pad">
        <ed-card class="overview-card col-md-12" footer-button="false">
          <div slot="body" class="soft-text">
            <div class="col-md-2">
              <strong>Card number</strong>
              <div>
                <div>
                  *{{ transaction.data.CardNumber }}
                </div>
              </div>
            </div>
            <div class="col-md-2">
              <strong>Card Holder</strong>
              <div>
                <div>
                  {{ formatHolderLine(transaction.data) }}
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <strong>Merchant</strong>
              <div v-tooltip="transaction.data.Merchant">
                <div class="overflow-merchant">{{ transaction.data.Merchant }}</div>
              </div>
            </div>
            <div class="col-md-2">
              <strong>Date</strong>
              <div>
                <div>{{ formatDate(transaction.data.TransactionDate) }}</div>
              </div>
            </div>
            <div class="col-md-2">
              <strong>Amount</strong>
              <div>
                <v-money currency="USD" locale="en">{{ transaction.data.Amount }}</v-money>
              </div>
            </div>
            <br />
          </div>
        </ed-card>
      </div>
      <div class="middle-band col-md-12 no-pad">
        <div class="middle-band-left no-pad col-md-6">
          <div class="col-md-12" style="margin-top:20px">
            <strong>{{ programName }}</strong>
            <ed-select :placeholder="`Select a ${ programName.toLowerCase() }...`"
                       :options="programLoader.items"
                       track-by="Id"
                       v-model="selectedProgram"
                       :value="selectedProgram"
                       :checkboxes="false"
                       :customLabel="option => option && option.Name ? (option.InternalProgramID ? `${option.Name} (${option.InternalProgramID})` : option.Name) : ''"
                       @open="onSelectProgramOpen"
                       @scroll-bottom="() => loadPrograms(false)"
                       server-search
                       @search="onProgramSearch"
                       name="program"
                       @input="handleProgramInput"
                       :disabled="reconcileAction === 'view' || (reconcileAction !== 'view' && transaction.data.TransactionStatus === 'In Envelope')"
                       v-tooltip="reconcileAction !== 'view' && transaction.data.TransactionStatus === 'In Envelope' ?
                        `This transaction is part of an envelope. You can now only change the ${ programName.toLowerCase() } from the envelope.` : ''"/>
          </div>
          <div class="col-md-12">
            <strong>Supplier</strong>
            <ed-select :placeholder="supplierPlaceholder"
                       :options="supplierOptions"
                       v-model="selectedSupplier"
                       :value="selectedSupplier"
                       :checkboxes="false"
                       :customLabel='getLabel'
                       required
                       @open='fetchSuppliersOnOpen'
                       @search="fetchSuppliers"
                       server-search
                       @select="getSelectedSupplier"
                       :disabled="true"
                       class="custom-dropdown-supplier"
                       @input="handleSupplierSelection">
              <template slot='option'
                        slot-scope='props'>
                <ed-select-options thumbnails>
                  <div slot='row1' class="option-combo-img">
                    <img slot='image' :src='props.option.Value.Logo' width='48' />
                  </div>
                  <div slot='row2' class="no-wrap">
                    <span class="option-strong"> <strong>{{ he.decode(props.option.Value.CompanyName) }} </strong> </span>
                    <em class="option-em">({{props.option.Value.Vci}})</em>
                    <br />
                    <span v-if='props.option.Value.SupplierName' class="option-bold"> {{ he.decode(props.option.Value.SupplierName) }}</span>
                    <span v-if='props.option.Value.SupplierRequestedName'>
                      <br />
                      <i>{{ props.option.Value.SupplierRequestedName }}</i>
                    </span>
                    <span v-if='props.option.Value.CountryName'>
                      <br />
                      <span class="fas fa-map-marker-alt contact-icon"></span> &nbsp;{{ props.option.Value.City + (props.option.Value.State != '' && props.option.Value.State != null ? ' ' + props.option.Value.State : (props.option.Value.Region != '' && props.option.Value.Region != null ? ' ' + props.option.Value.Region : '')) + ' ' + props.option.Value.CountryName }}
                    </span>
                    <span v-if='props.option.Value.BusinessType'>
                      <br />
                      <span class="fas fa-briefcase contact-icon"></span> &nbsp;{{ props.option.Value.BusinessType }}
                    </span>
                    <span v-if='props.option.Value.SupplierEmail'>
                      <br />
                      <span class="fas fa-envelope contact-icon">
                      </span> &nbsp;{{ props.option.Value.SupplierEmail }}
                    </span>
                    <br />
                    <span v-if='props.option.Value.IsEvedPay' class="label supplier-evedpay">EvedPay Ready</span>
                    <span v-if='props.option.Value.VendorType' class="label" :class="[`supplier-type-${props.option.Value.VendorType}`]">{{ props.option.Value.VendorType }} Supplier</span>
                  </div>
                </ed-select-options>
              </template>
            </ed-select>
            <div class="clear"></div>
          </div>
          <div class="col-md-12 description-field">
            <strong>Description <span class="optional-text" v-if="!this.requireReceiptDescription">- optional</span></strong>
            <div>
              <textarea class="description-textarea" v-model="noteText" rows="4" @input="handleTextDescription" :disabled="reconcileAction === 'view'"></textarea>
              <span v-if="fieldsIncomplete.descriptionIncomplete" style="color: red;">Please add a description.</span>
            </div>
          </div>
          <!-- PRODUCTDEV-5802 — `loading` is the global old-UI spinner+overlay
               (main.scss:134), shown while a replacement / no-scan receipt upload
               is in flight. These paths have no 4s progress bar, so without this
               the upload window looked like nothing was happening. -->
          <div class="col-md-12 receipt-section" :class="{ loading: receiptUploadInProgress }">
            <strong>Receipt</strong>
            <div v-if="!receiptScanEnabled" class="receipt-upload-legacy">
              <ed-upload button-label='Upload New Receipt'
                         button-position="bottom"
                         :class='{loading: !uploadFinished}'
                         v-model='attachedFiles'
                         :disabled="reconcileAction === 'view'"
                         v-tooltip="reconcileAction === 'view' ? permissionTooltip : ''"
                         readonly
                         :multiple='true'
                         :postAction='api.env + api.$.evedpaycard.receipt'
                         :uploadAuto='true'
                         :canOpenFile='true'
                         @input='createAttachments'
                         :headers='{Authorization: "Bearer " + uploadData.token}'
                         name='attachment_upload'
                         accept='application/pdf, image/png, image/jpeg'
                         extensions='pdf,png,jpg,jpeg'
                         xtarget='transactionAttachment'
                         @open-file='openAttachment'
              >
              </ed-upload>
            </div>
            <div v-if="receiptScanEnabled && hasAttachment" class="receipt-file-chip">
              <i class="fas" :class="primaryAttachmentIconClass"></i>
              <a :href="previewUrl || undefined" target="_blank" rel="noopener" class="receipt-file-chip-name" @click="openPrimaryAttachment">{{ previewFileName }}</a>
              <span class="receipt-file-chip-size">{{ formatBytes(primaryAttachment && primaryAttachment.size, 0) }}</span>
              <a v-if="canManageReceipts" href="javascript:;" class="receipt-file-chip-remove" @click="removePrimaryAttachment" aria-label="Remove receipt">
                <i class="fas fa-times-circle"></i>
              </a>
            </div>
            <div v-if="receiptScanEnabled && uploadingReceipt && reconcileAction !== 'view'" class="receipt-upload-progress">
              <div class="receipt-upload-progress-track">
                <div class="receipt-upload-progress-fill" :style="{ width: uploadProgressPct + '%' }"></div>
              </div>
              <span class="receipt-upload-progress-label">{{ uploadProgressPct }}%</span>
            </div>
            <!-- PRODUCTDEV-5783 — once a receipt is attached these buttons stay visible but are
                 DISABLED (not clickable); clearing the receipt with the ✕ re-enables them.
                 Replace/Rescan logic is unchanged and still reached via the post-✕ re-upload.
                 PRODUCTDEV-5790 — view mode (transaction in a submitted / non-Draft envelope, or
                 otherwise read-only) now renders this SAME block with both buttons DISABLED, so a
                 submitted-envelope transaction matches the create / Draft-envelope layout instead
                 of falling back to the old single-button legacy uploader. The hover message sits on
                 a wrapping <span> because a disabled native <button> swallows hover events, so
                 v-tooltip on the button itself never fires (same wrapper pattern the coding-table
                 disabled selects use). -->
            <div v-if="receiptScanEnabled" class="receipt-scan-actions">
              <!-- PRODUCTDEV-5788 — folder pick hidden when scanning is disabled (no scan/match).
                   Also gated on receiptScanModeLoaded so it never flashes in before the mode is
                   known; the block itself always renders (Upload new receipt shows in every mode),
                   so Additional Documents below never reflows while the mode resolves. No-Capture
                   hides the folder button in view mode too — create / Draft hide it there as well. -->
              <span v-if="!receiptCaptureDisabled && receiptScanModeLoaded"
                    v-tooltip="!canManageReceipts ? permissionTooltip : (hasAttachment ? 'Remove the current receipt to upload a different one' : '')">
                <button type="button" class="receipt-action-button"
                        :disabled="!canManageReceipts || hasAttachment"
                        @click="openFolderPicker">Add from receipt folder</button>
              </span>
              <span v-tooltip="!canManageReceipts ? permissionTooltip : (hasAttachment ? 'Remove the current receipt to upload a different one' : '')">
                <button type="button" class="receipt-action-button"
                        :disabled="!canManageReceipts || hasAttachment"
                        @click="uploadNewReceipt">Upload new receipt</button>
              </span>
            </div>
            <!-- PRODUCTDEV-5788 — the replace/rescan prompt never appears when scanning is disabled (no scan ever runs). -->
            <div v-if="receiptScanEnabled && !receiptCaptureDisabled && receiptReplacedPendingRescan && reconcileAction !== 'view'" class="replace-coding-alert">
              <span class="replace-coding-alert-text">{{ replaceCodingMessage }}</span>
              <a href="javascript:;" class="replace-coding-alert-action" @click="rescanReceipt">{{ replaceCodingActionLabel }}</a>
            </div>
          </div>
          <!-- Desktop Additional Documents — upload-only (PRODUCTDEV-5783/5772). The mobile
               surface renders its own layout below; gating this with !isMobile is provably
               identical on desktop (isMobile is always false there). -->
          <div v-if="receiptScanEnabled && !isMobile" class="col-md-12 additional-documents" :class="{ loading: documentUploadInProgress }">
            <strong>Additional Documents</strong>
            <div v-for="(doc, docIndex) in additionalDocuments" :key="`doc-${docIndex}`" class="receipt-file-chip">
              <i class="fas" :class="documentIconClass(doc)"></i>
              <a :href="doc.path || undefined" target="_blank" rel="noopener" class="receipt-file-chip-name" @click="openDocument(doc, $event)">{{ doc.name }}</a>
              <span class="receipt-file-chip-size">{{ formatBytes(doc.size, 0) }}</span>
              <a v-if="canManageReceipts" href="javascript:;" class="receipt-file-chip-remove" @click="removeDocument(docIndex)" aria-label="Remove document">
                <i class="fas fa-times-circle"></i>
              </a>
            </div>
            <!-- PRODUCTDEV-5783 — additional documents are upload-only (no "Add from Receipt Folder"). -->
            <div v-if="canManageReceipts" class="receipt-scan-actions">
              <button type="button" class="receipt-action-button" @click="uploadNewDocument">Upload file</button>
            </div>
          </div>
          <!-- Mobile Additional Documents (Figma) — upload-only, matching the desktop block
               above. The "Add from Receipt Folder" button was removed by product request; the
               only way to attach an additional document is UPLOAD FILE. -->
          <div v-if="receiptScanEnabled && isMobile" class="col-md-12 additional-documents" :class="{ loading: documentUploadInProgress }">
            <strong>Additional Documents</strong>
            <div v-for="(doc, docIndex) in additionalDocuments" :key="`mdoc-${docIndex}`" class="receipt-file-chip">
              <i class="fas" :class="documentIconClass(doc)"></i>
              <a :href="doc.path || undefined" target="_blank" rel="noopener" class="receipt-file-chip-name" @click="openDocument(doc, $event)">{{ doc.name }}</a>
              <span class="receipt-file-chip-size">{{ formatBytes(doc.size, 0) }}</span>
              <a v-if="canManageReceipts" href="javascript:;" class="receipt-file-chip-remove" @click="removeDocument(docIndex)" aria-label="Remove document">
                <i class="fas fa-times-circle"></i>
              </a>
            </div>
            <div v-if="canManageReceipts" class="receipt-scan-actions">
              <button type="button" class="receipt-action-button" @click="uploadNewDocument">UPLOAD FILE</button>
            </div>
          </div>
        </div>
        <div v-if="hasAttachment" class="middle-band-right col-md-6 no-pad">
          <div class="col-md-12">
            <div class="receipt-preview-panel">
              <strong class="receipt-preview-label">Receipt</strong>
              <ReceiptImageViewer
                :image-url="previewUrl"
                :content-type="primaryAttachment && primaryAttachment.type"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-if="amountMismatch && reconcileAction !== 'view'" class="amount-mismatch-banner">
        <i class="fas fa-exclamation-triangle"></i>
        <span>Amount mismatch — please review your coding.</span>
      </div>
      <div class="col-md-12 no-pad coding-section" v-if="form">
        <strong>Coding</strong>
        <div class="col-md-12 no-pad">
          <table class="full-width-table">
            <thead>
              <tr>
                <th v-if="reconcileAction !== 'view'"></th>
                <th v-if="receiptScanEnabled" class="description-col">Description</th>
                <th v-if="hasBu && !hasBuAndCcPair">Business Units</th>
                <th v-if="hasCc && !hasBuAndCcPair">Cost Centers</th>
                <th v-if="hasBuAndCcPair">Bu & Cc</th>
                <th>Category</th>
                <th>Category Ref ID</th>
                <th v-for="(field, index) in form[0]" :key="index">
                  <span v-if="field.Key !== 'Amount'">{{ field.Label }}</span>&nbsp;
                </th>
                <th class="amount-col">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="codingRowsLoading">
                <td colspan="20" class="coding-row-skeleton-cell">
                  <span class="skeleton skeleton-line coding-row-skeleton"></span>
                </td>
              </tr>
              <tr v-for="(row, rowIndex) in form" :key="`form-row-${rowIndex}-${splitCounter}`" v-show="!codingRowsLoading">
                <td class="split-buttons" v-if="reconcileAction !== 'view'">
                  <a href="javascript:;"
                     class="button row-action"
                     @click="(!selectedProgram || Object.keys(selectedProgram).length === 0) ? showWarningMessage(`Please select a ${programName.toLowerCase()} before adding items`) : toggleSplit(rowIndex)">
                    + Split
                  </a>
                  &nbsp;
                  <a href="javascript:;"
                     class="button row-action"
                     @click="copySplit(rowIndex)">
                    Copy
                  </a>
                  &nbsp;
                  <a href="javascript:;"
                     class="button row-action"
                     @click="pasteSplit(rowIndex)">
                    Paste
                  </a>
                  &nbsp;
                  <a href="javascript:;"
                     v-if="splitCounter > 1"
                     class="button row-action"
                     @click="removeSplit(rowIndex)">
                    Remove
                  </a>
                </td>
                <td v-if="receiptScanEnabled" class="description-col">
                  <span v-if="scanningReceipt" class="skeleton skeleton-line skeleton-description"></span>
                  <ed-input v-else
                            class="no-margin borderless description-input"
                            v-model="descriptions[rowIndex]"
                            :value="descriptions[rowIndex]"
                            :disabled="reconcileAction === 'view'"
                            @input="(value) => handleDescription(value, rowIndex)" />
                </td>
                <td v-if="hasBu && !hasBuAndCcPair"
                    v-tooltip="(!selectedProgram || Object.keys(selectedProgram).length === 0) ? `Please select a ${ programName.toLowerCase() } first` :
                            businessUnits.length === 0 ? `Please add Business Units to the selected ${ programName.toLowerCase() } before proceeding` : ''">
                  <ed-select class="borderless"
                             placeholder="Select a BU..."
                             :options="businessUnits"
                             v-model="selectedBu[rowIndex]"
                             :value="selectedBu[rowIndex]"
                             :checkboxes="false"
                             label="name"
                             track-by="businessUnitId"
                             name="bu"
                             :disabled="reconcileAction === 'view' || (!selectedProgram || Object.keys(selectedProgram).length === 0) || businessUnits.length === 0"
                             @input="(option) => handleBUSelection(option, rowIndex)"
                             :style="{ marginBottom: '0' }" />
                </td>
                <td v-if="hasCc && !hasBuAndCcPair"
                    v-tooltip="(!selectedProgram || Object.keys(selectedProgram).length === 0) ? `Please select a ${ programName.toLowerCase() } first` :
                            costCenters.length === 0 ? `Please add Cost Centers to the selected ${ programName.toLowerCase() } before proceeding` : ''">
                  <ed-select class="borderless"
                             placeholder="Select a CC..."
                             :options="costCenters"
                             v-model="selectedCc[rowIndex]"
                             :value="selectedCc[rowIndex]"
                             :checkboxes="false"
                             :custom-label="(o) => `${o.code} (${o.name})`"
                             track-by="costCenterId"
                             name="cc"
                             :disabled="reconcileAction === 'view' || (!selectedProgram || Object.keys(selectedProgram).length === 0) || costCenters.length === 0"
                             @input="(option) => handleCCSelection(option, rowIndex)"
                             :style="{ marginBottom: '0' }" />
                </td>
                <td v-if="hasBuAndCcPair"
                    v-tooltip="(!selectedProgram || Object.keys(selectedProgram).length === 0) ? `Please select a ${ programName.toLowerCase() } first` :
                            buAndCcPairs.length === 0 ? `Please add BUs & CCs to the selected ${ programName.toLowerCase() } before proceeding` : ''">
                  <ed-select class="borderless"
                             placeholder="Select BU & CC..."
                             :options="buAndCcPairs"
                             v-model="selectedBuAndCcPair[rowIndex]"
                             :value="selectedBuAndCcPair[rowIndex]"
                             :checkboxes="false"
                             label="name"
                             track-by="programBusinessUnitAndCostCenterId"
                             name="cc"
                             :disabled="reconcileAction === 'view' || (!selectedProgram || Object.keys(selectedProgram).length === 0) || buAndCcPairs.length === 0"
                             @input="(option) => handleBuAndCcPairSelection(option, rowIndex)"
                             :style="{ marginBottom: '0' }" />
                </td>
                <td class="predicted-category-cell"
                    :class="{ 'has-predicted-coding-icon': allowPredictiveCoding && predictedCategory[rowIndex] && selectedCategory[rowIndex] }"
                    v-tooltip="(!selectedProgram || Object.keys(selectedProgram).length === 0) ? `Please select a ${ programName.toLowerCase() } first` :
                            categories.length === 0 ? `Please assign Categories to the ${ divisionName.toLowerCase() } of the selected ${ programName.toLowerCase() } before proceeding` : ''">
                  <ed-select class="borderless"
                             placeholder="Select a Category..."
                             :options="categories"
                             v-model="selectedCategory[rowIndex]"
                             :value="selectedCategory[rowIndex]"
                             :checkboxes="false"
                             label="text"
                             track-by="key"
                             name="category"
                             :disabled="reconcileAction === 'view' || (!selectedProgram || Object.keys(selectedProgram).length === 0) || categories.length === 0"
                             @input="(option) => handleCategorySelection(option, rowIndex)"
                             :style="{ marginBottom: '0' }" />
                  <span v-if="allowPredictiveCoding && predictedCategory[rowIndex] && selectedCategory[rowIndex]"
                        class="predicted-coding-icon-container">
                    <predicted-coding-icon />
                  </span>
                </td>
                <td v-tooltip="(!selectedProgram || Object.keys(selectedProgram).length === 0) ? `Please select a ${ programName.toLowerCase() } first` :
                            categories.length === 0 ? `Please assign Categories to the ${ divisionName.toLowerCase() } of the selected ${ programName.toLowerCase() } before proceeding` :
                            !selectedCategory[rowIndex] ? `Please select a category first` : ''">
                  <ed-input class="no-margin borderless"
                            placeholder="Category Ref ID..."
                            v-model="referenceId[rowIndex]"
                            :value="referenceId[rowIndex]"
                            :disabled="reconcileAction === 'view' || (!selectedProgram || Object.keys(selectedProgram).length === 0) || categories.length === 0 || !selectedCategory[rowIndex]"
                            @input="(value) => handleReferenceID(value, rowIndex)"
                            maxlength="50" />
                </td>
                <template v-for="(field, fIndex) in row">
                  <td v-if="field.Key !== 'Amount'" :key="`field-${rowIndex}-${fIndex}`">
                    <input v-if="field.Type !== 'select'"
                           class="custom-fields"
                           type="text" size="1"
                           style="width: 100%; border:none"
                           v-model="field.Values"
                           :placeholder="field.Label"
                           maxlength="200"
                           :disabled="reconcileAction === 'view'">
                    <ed-select v-if="field.Type === 'select'"
                               :placeholder="`${ field.Label }...`"
                               :options="customOptions[field.Key]"
                               track-by="value"
                               v-model="field.Values"
                               :checkboxes="false"
                               label="text"
                               name="program"
                               class="borderless"
                               style="width: 120px; margin: 0" />
                  </td>
                </template>
                <td class="amount-col">
                  <span v-if="scanningReceipt && receiptScanEnabled" class="skeleton skeleton-line skeleton-amount-cell"></span>
                  <template v-else>
                    <ed-input class="money-input borderless"
                              v-if="splitCounter > 1"
                              amount
                              type="money"
                              v-model="row.Amount"
                              :key="`amount-input-${rowIndex}-${splitCounter}`"
                              @input="handleSplitAmounts"
                              :disabled="reconcileAction === 'view'" />
                    <v-money v-else currency="USD" locale="en">{{ form[0] && form[0].Amount != null ? form[0].Amount : transaction.data.Amount }}</v-money>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <ed-modal title="Caution"
              :show="showAlert"
              @close="showAlert = false, selectedProgram = programConfirmPressed ? programOption : previousSelectedProgram, programConfirmPressed = false"
              @confirm="handleProgramChange(programOption), programConfirmPressed = true"
              :primary-button="'YES'"
              :secondary-button="'NO'">
      <div slot="body">
        <p class="small">Changing the Program will clear the Business Unit, Cost Center, and Category selections on every split row (descriptions and amounts stay). Are you sure?</p>
      </div>
    </ed-modal>
    <iframe name="transactionAttachment" id="transactionAttachment" class="transaction-attachment" src="about:blank"></iframe>
    <a @click="closeAttachment" class="close-attachment-button"><i class="fa fa-times-circle"></i></a>
    <receipt-folder-picker
      :show="showFolderPicker"
      :is-processing="folderPickerIsProcessing"
      :transaction-id="isMobile ? String(transaction.data.Id) : null"
      @select="handleFolderPickerSelect"
      @close="closeFolderPicker"
    />
  </form>
</template>

<script>
import he from 'he'
import { EventBus } from '@/event-bus'
import receiptScanMixin from '@/mixins/receiptScanMixin'
import featureFlags from '@/core/feature-flags/feature-flags'
import receiptScansApi from '@/core/api/receiptScans'
import ReceiptFolderPicker from '@/components/_modules/Programs/Card-transactions/ReceiptFolderPicker.vue'
import ReceiptImageViewer from '@/components/_modules/Programs/Card-transactions/ReceiptImageViewer.vue'
import PredictedCodingIcon from '@/components/_modules/Programs/Card-transactions/PredictedCodingIcon.vue'

// Predictive Coding prefill (EvedPay Card reconcile surface).
// surface = PredictionSurface.EpcReconcile, sent to the Platform predict API as the integer 1.
const EPC_RECONCILE_SURFACE = 1
// Tight client-side cap so a slow predict endpoint can never hang the silent prefill.
const PREDICT_TIMEOUT_MS = 8000

export default {

  components: {
    ReceiptFolderPicker,
    ReceiptImageViewer,
    PredictedCodingIcon
  },

  mixins: [receiptScanMixin],

  data() {
    return {
      receiptScanEnabled: featureFlags.receiptScanPrefill,
      // PRODUCTDEV-5788 — per-division "No Receipt Capture" gate. Default false so a
      // fetch failure never hides the scan affordances on an enabled division. Set in
      // created() from the division's receipt-scan mode (scanning_disabled). When true:
      // hide the folder + replace-coding controls and attach uploads WITHOUT Photon.
      // The GLOBAL receiptScanEnabled flag (above) is untouched, so coding + the
      // Description column + Additional Documents keep rendering (forward-only, per §5).
      receiptCaptureDisabled: false,
      // PRODUCTDEV-5788 — false until the division's scan mode resolves; the scan-actions
      // block waits on this so "Add from receipt folder" doesn't flash in for a frame before
      // "No Receipt Capture" is known. Set true after the getScanMode fetch (or when skipped).
      receiptScanModeLoaded: false,
      // PRODUCTDEV-5805 — DivisionId of the transaction being reconciled; sent to /prefill so a
      // folder-picked (unmatched) receipt's prefill matches this division's selected scan mode
      // instead of the BE default (subtotal_and_tax).
      receiptScanDivisionId: null,
      receiptScanPrefillApplied: false,
      receiptScanConfidence: null,
      receiptScanPrefillMode: null,
      // amount_mismatch from prefill — surfaced via the warning banner above
      // the Coding section. The BE no longer falls back to total_only on
      // mismatch; instead it preserves Photon's breakdown and asks us to warn.
      amountMismatch: false,
      receiptScanPhotonTotal: null,
      uploadingReceipt: false,
      uploadProgressPct: 0,
      // PRODUCTDEV-5802 — old-UI loading spinner (global `.loading` class, same
      // affordance the legacy ed-upload showed via `{loading: !uploadFinished}`)
      // over the Receipt section during plain byte-upload windows that have no
      // 4s bar: replaceReceiptFile (re-upload once coding/receipt exists) and
      // attachReceiptWithoutScan (No Receipt Capture mode). The 4s bar stays
      // reserved for windows where Photon actually scans (fresh first upload,
      // Rescan Receipt).
      receiptUploadInProgress: false,
      // PRODUCTDEV-5802 (reopen, Case 3) — same old-UI spinner over the
      // Additional Documents section while an Upload-file byte-upload is in
      // flight, with Save gated for the window.
      documentUploadInProgress: false,
      scanningReceipt: false,
      scanCompleted: false,
      // Full-screen "Uploading receipt…" overlay during UPLOAD NEW RECEIPT's
      // upload+Photon round-trip. Mobile-only (isMobile) — desktop keeps its
      // inline receipt-upload-progress bar. Set true once the fresh-scan flow
      // starts (past validation) and cleared by the scanCompleted watcher, which
      // every terminal branch of the upload flow flips to true.
      uploadingScanOverlay: false,
      // PRODUCTDEV-5772 — receipt replacement. When a receipt already exists and the
      // user uploads/picks a new one, the file is swapped WITHOUT scanning and this
      // flag raises the "Replace coding?" alert. pendingRescanFile holds the new File
      // so "Rescan Receipt" can run Photon on it on demand. Both cleared after rescan,
      // chip-remove, or a fresh first-run upload.
      receiptReplacedPendingRescan: false,
      pendingRescanFile: null,
      // PRODUCTDEV-5783 — when a REPLACE came from the receipt folder (which already has scan
      // data), this holds that folder scan's id so "Replace coding" applies its EXISTING prefill
      // (no re-scan). Null for an upload-replace, which re-scans via pendingRescanFile.
      pendingFolderPrefillScanId: null,
      // PRODUCTDEV-5772 — true when the reconcile was opened with saved coding rows
      // (backend returned TransactionFields). The most reliable "coding already exists"
      // signal — covers single-row / category-only / amount-only coding that has no
      // split count or description to detect. Drives hasCodingOrReceipt.
      loadedWithCoding: false,
      descriptions: [],
      showFolderPicker: false,
      folderPickerIsProcessing: false,
      // PRODUCTDEV-5772 — supporting documents, kept entirely separate from receipts
      // (attachedFiles). Never Photon-scanned, never affect status / IsComplete.
      additionalDocuments: [],
      programs: [],
      noteText: '',
      suppliers: [],
      newReconcile: {},
      selectedSupplier: Object,
      selectedProgram: null,
      supplierOptions: [],
      programOptions: [],
      splitCounter: 1,
      mediaFilesPath: '',
      form: null,
      requireReceiptDescription: false,
      attachedPath: [],
      uploadData: {
        token: this.$user.sessionSettings.Token
      },
      splitAmounts: this.transaction.data.Amount,
      attachedFiles: [],
      transactionFiles: [],
      businessUnits: [],
      costCenters: [],
      buAndCcPairs: [],
      categories: [],
      selectedBu: [],
      selectedCc: [],
      selectedBuAndCcPair: [],
      selectedCategory: [],
      predictedCategory: [],
      referenceId: [],
      programName: '',
      divisionName: '',
      loadedCategoriesAndBuAndCc: -1,
      allowPredictiveCoding: false,
      // True from mount until setRowFieldsValues actually writes per-row
      // values into the form. Hides the empty-cells flash that otherwise
      // shows while getCategoriesAndBuAndCc + the sequential per-select
      // getCustomOptions network calls finish.
      codingRowsLoading: true,
      hasBu: false,
      hasCc: false,
      hasBuAndCcPair: false,
      showAlert: false,
      programOption: Object,
      previousSelectedProgram: Object,
      programConfirmPressed: false,
      copySplitObject: {},
      customOptions: {},
      baseCustomFields: null,
      uploadFinished: true,
      programLoader: {
        items: [],
        pageIndex: 0,
        hasMore: true,
        isLoading: false,
        searchTerm: ''
      }
    }
  },

  props: {
    transaction: Object,
    fieldsIncomplete: Object,
    reconcileAction: 'create',
    // True only when mounted by the mobile EvedPay surface
    // (EvedPay/transactiondetails.vue). Gates mobile-only UI (the full-screen
    // upload overlay, the mobile Additional Documents layout) so the shared
    // desktop Reconcile modal stays unchanged. Same pattern as CandidateSelector's is-mobile.
    isMobile: {
      type: Boolean,
      default: false
    }
  },

  beforeCreate() {
    this.he = he
  },

  async created() {
    try {
      let row = this.transaction.data

      if (row.ProgramId) {
        this.selectedProgram = {
          Id: row.ProgramId,
          Name: row.ProgramName,
          InternalProgramID: row.InternalProgramID
        }

        this.getCategoriesAndBuAndCc(row.ProgramId)
      }

      if (row.Supplier || row.NonEvedSupplier) {
        let supplierName = row.Supplier ? `${row.Supplier} (#${row.SupplierEoNumber})` : row.NonEvedSupplier
        this.selectedSupplier = {
          Key: row.VendorId,
          Value: {
            CompanyName: supplierName,
            VendorId: row.SupplierId || -row.SupplierNetworkId
          }
        }
      }

      await this.getReconcile(row)

      // PRODUCTDEV-5788 — resolve the division's receipt-scan mode BEFORE any
      // auto-scan/prefill so "No Receipt Capture" suppresses the scan. Default the flag
      // false on any failure (never hide UI for an enabled division).
      if (this.receiptScanEnabled && row.DivisionId != null) {
        // PRODUCTDEV-5805 — remember the division so tryApplyReceiptScanPrefill can tell the BE
        // which division's mode to apply when prefilling an unmatched folder receipt.
        this.receiptScanDivisionId = row.DivisionId
        try {
          var scanSetting = await receiptScansApi.getScanMode(row.DivisionId)
          this.receiptCaptureDisabled = !!(scanSetting && scanSetting.prefillMode === 'scanning_disabled')
        } catch (e) {
          console.warn('Could not fetch receipt-scan mode (defaulting to enabled):', e)
          this.receiptCaptureDisabled = false
        }
      }
      // PRODUCTDEV-5788 — mode resolved (or not applicable); reveal the scan actions now so
      // the folder button never renders before the No-Capture state is known.
      this.receiptScanModeLoaded = true

      // One-shot receipt-scan prefill from query param
      await this.tryApplyReceiptScanPrefill()

      // Start poller if receiptScanId is present and scan is still processing
      var receiptScanId = this.$route.query.receiptScanId
      if (receiptScanId && this.receiptScanEnabled) {
        this.startReceiptScan(receiptScanId, { transactionId: this.transaction.data.Id })
        // Consume any Legacy-upload metadata stashed by Card-list's Dashboard
        // SUBMIT flow. Without this the matched-via-Dashboard reconcile has no
        // TransactionFiles row, and reopen-view crashes (Substring(0,-1)).
        // One-shot: delete the key after consuming so a refresh or different
        // tab doesn't double-attach.
        this.consumeStashedLegacyReceipt(receiptScanId)
        // Mobile FAB flow: upload happened in mobile.vue against Platform only and
        // matchToTransaction (SubmitReceipt → Done) is Platform-only too, so Legacy
        // /get-reconcile returned TransactionFiles=[] and populateFiles did nothing.
        // Bridge by fetching Platform's FileInfo + presigned URL and pushing a
        // synthetic attachment so hasAttachment flips true and the image renders.
        // Save-time createReconcile reads filteredAttachments (incl. ReceiptScanId),
        // Legacy EvedPayCardsRepository writes a TransactionFile row, and
        // ConfirmLegacySubmit advances the scan from Matched → Submitted.
        if (this.isMobile && this.attachedFiles.length === 0) {
          await this.attachReceiptScanIfMissing(receiptScanId)
        }
      }

      this.programName = this.singularize(this.$root.getUser.settings.Labels.ProgramLabel)
      this.divisionName = this.singularize(this.$root.getUser.settings.Labels.DivisionName)

      this.hasBu = this.$root.getUser.settings.HasBusinessUnit
      this.hasCc = this.$root.getUser.settings.HasCostCenter
      this.hasBuAndCcPair = this.$root.getUser.settings.UseBusinessUnitAndCostCenterMapping
      this.hasBuOrCcOrPair = this.hasBu || this.hasCc || this.hasBuAndCcPair

      this.$emit('split-amounts', this.splitAmounts)
    } catch (error) {
      console.error('Error during component initialization:', error)
      throw error
    }
  },

  computed: {
    supplierPlaceholder() {
      let row = this.transaction.data
      return row.Merchant ? row.Merchant : 'Select a supplier...'
    },
    isUserAdmin() {
      if (this.$user.$.userPermissions.includes('EvedPayCardAdministrator')) {
        return true
      }
      return false
    },
    permissionTooltip() {
      return 'You do not have permission to change receipt attachments on this transaction.'
    },
    // PRODUCTDEV-5790 — receipt + additional-document editing is allowed whenever the
    // reconcile is editable (create/edit), OR for a card admin even on a view-locked
    // (submitted-envelope) transaction. This restores the PRODUCTDEV-4071 admin exception
    // that the V2 receipt-capture rework dropped. Coding stays read-only in view mode
    // regardless (gated separately on reconcileAction === 'view'); admins persist a
    // receipt/document-only change through the narrow update-receipt endpoint in the host.
    // Current approvers don't need a clause here — they already resolve to 'edit'.
    canManageReceipts() {
      return this.reconcileAction !== 'view' || this.isUserAdmin
    },
    hasAttachment() {
      return Array.isArray(this.attachedFiles) && this.attachedFiles.some(f => f && f.success !== false)
    },
    // PRODUCTDEV-5772 — a brand-new receipt upload auto-scans Photon ONLY on a
    // fresh/default reconcile: no coding entered yet AND no receipt attached. The
    // moment coding exists (a scan ran, or saved coding was loaded, or the user
    // split/described rows) OR a receipt is attached, an upload instead swaps the
    // receipt and offers "Rescan Receipt" — so existing coding is never silently
    // overwritten. Additional Documents are deliberately NOT considered here.
    hasCodingOrReceipt() {
      if (this.hasAttachment) return true
      // Opened with saved coding (any shape — single row, category-only, etc.).
      if (this.loadedWithCoding) return true
      // Coding entered/scanned this session: extra split rows, descriptions, or any
      // category / BU / CC / pair / reference-id selected on a row.
      if (Array.isArray(this.form) && this.form.length > 1) return true
      if (Array.isArray(this.descriptions) && this.descriptions.some(d => d && String(d).trim() !== '')) return true
      var selections = [this.selectedCategory, this.selectedBu, this.selectedCc, this.selectedBuAndCcPair]
      if (selections.some(arr => Array.isArray(arr) && arr.some(v => v != null))) return true
      if (Array.isArray(this.referenceId) && this.referenceId.some(v => v != null && v !== '')) return true
      return false
    },
    // PRODUCTDEV-5783 — the "Replace coding?" alert text + action differ by source: a folder
    // replace applies the folder receipt's existing prefill; an upload replace re-scans via Photon.
    replaceCodingMessage () {
      return this.pendingFolderPrefillScanId
        ? 'Receipt added from the folder. Replace the current coding with the folder receipt\'s?'
        : 'A new receipt was uploaded. Replace coding?'
    },
    replaceCodingActionLabel () {
      return this.pendingFolderPrefillScanId ? 'Replace coding' : 'Scan Receipt'
    },
    primaryAttachment() {
      if (!this.hasAttachment) return null
      return this.attachedFiles.find(f => f && f.success !== false) || null
    },
    previewFileName() {
      var f = this.primaryAttachment
      return (f && (f.name || f.file)) || ''
    },
    previewUrl() {
      var f = this.primaryAttachment
      if (!f) return ''
      return f.path || ''
    },
    primaryAttachmentIconClass() {
      var name = (this.previewFileName || '').toLowerCase()
      if (/\.pdf$/.test(name)) return 'fa-file-pdf'
      if (/\.(png|jpe?g|gif|webp)$/.test(name)) return 'fa-file-image'
      return 'fa-file'
    }
  },

  watch: {
    // Clear the mobile upload overlay the moment the upload+scan reaches a terminal
    // state. uploadNewReceipt's fresh-scan flow sets scanCompleted=true at every
    // success/failure branch (the rollback blocks + tryApplyReceiptScanPrefill's
    // finally + the receiptScan.status watcher below), so this single watcher covers
    // them all. Inert outside the upload flow: the flag is already false there.
    scanCompleted: function (newVal) {
      if (newVal === true) {
        this.uploadingScanOverlay = false
      }
    },
    'receiptScan.status': function (newStatus) {
      var self = this
      var terminalFailure = newStatus === 'Failed' ||
        newStatus === 'PlatformUnreachable' ||
        newStatus === 'PollingTimeout'

      if (newStatus === 'Complete') {
        // Guard: tryApplyReceiptScanPrefill (the inline /api/v4 sync path) already
        // applies amounts immediately after submit. The poller-driven Complete event
        // arrives later. With reconcile uploads always auto-linked (BE change), the
        // poller's autoFill data would re-run applyAmountPrefill and overwrite the
        // form — including writing nulls when photonSubtotal/photonTax both ==0 —
        // making the amount cells appear empty. Skip the redundant pass when prefill
        // already landed.
        if (!self.receiptScanPrefillApplied &&
            self.receiptScan.autoLinked &&
            self.receiptScan.autoFill) {
          // ReceiptScanAutoFillDataDto: photonTotal/photonSubtotal/photonTax (no confidence here)
          var auto = self.receiptScan.autoFill
          self.applyAmountPrefill({
            totalAmount: auto.photonTotal,
            subtotalAmount: auto.photonSubtotal,
            taxAmount: auto.photonTax
          })
          self.receiptScanPrefillApplied = true
          self.receiptScanConfidence = null
        }
        // Photon is done — drop the skeleton. The inline-prefill path's finally
        // intentionally leaves these flags untouched on 409/404/sentinel so the
        // upload-bar's post-100% tick can engage the skeleton during the gap;
        // the watcher is responsible for clearing it once Photon lands.
        self.scanCompleted = true
        self.scanningReceipt = false
      } else if (terminalFailure) {
        // Photon never completed (failed / unreachable / poll timeout). Clear
        // the skeleton so the user can manually fill the form — the poller's
        // onError surfaces the user-facing message; no warning duplicated here.
        self.scanCompleted = true
        self.scanningReceipt = false
      }
    }
  },

  methods: {
    // Surface the custom Name on Card next to the holder ("Jane Doe | John Smith") when set and
    // meaningfully differs, mirroring the CardHolder.vue two-line layout the cards/envelopes grid renders.
    // See PRODUCTDEV-5553 for the underlying pattern.
    formatHolderLine(rowData) {
      const holder = ((rowData && rowData.Holder) || '').trim()
      const nameOnCard = ((rowData && rowData.NameOnCard) || '').trim()
      if (!nameOnCard || nameOnCard.toLowerCase() === holder.toLowerCase()) {
        return (rowData && rowData.Holder) || ''
      }
      if (!holder) {
        return nameOnCard
      }
      return `${nameOnCard} | ${holder}`
    },

    async getCustomOptions(url, key) {
      try {
        const response = await this.api.get(url)
        if (response && response.data) {
          this.customOptions[key] = response.data
        }
      } catch (error) {
        console.error(error)
      }
    },

    openAttachment(data) {
      data.e.preventDefault()

      // PRODUCTDEV-5783 — PDFs open in a new browser tab, matching how the rest of the app
      // opens files (window.open '_blank', e.g. envelope export). The in-app iframe can't
      // render cross-origin PDFs that send X-Frame-Options (Legacy/S3), so framing them left
      // the pane blank — that's why some receipt/document links "didn't work" for PDFs.
      if (data.file.type === 'application/pdf') {
        if (data.path) window.open(data.path, '_blank')
        return
      }

      // Images keep the in-app overlay viewer — renders fine via <embed>.
      const frame = window.document.getElementById('transactionAttachment')
      frame.srcdoc = `<style>embed {width: 99%}</style><embed src="${data.path}" type=${data.file.type} />`
      frame.classList.add('open')
      EventBus.$emit('openAttachment')
    },
    closeAttachment() {
      window.document.getElementById('transactionAttachment').classList.remove('open')
      window.document.getElementById('transactionAttachment').removeAttribute('srcdoc')
      EventBus.$emit('closeAttachment')
    },

    createAttachments(e) {
      e.forEach(x => {
        var parts = x.name.split('.')
        var extension = ''
        if (parts.length > 1) {
          extension = parts[parts.length - 1]
        }
        if (!['pdf', 'png', 'jpg', 'jpeg'].includes(extension.toLowerCase())) {
          this.attachedFiles = this.attachedFiles.filter(x => x.success)
          this.showWarningMessage(`The file "${x.name}" is not compatible. Please upload files with one of the following extensions: .pdf, .png, .jpg, .jpeg`)
          return
        }

        const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
        if (x.size >= MAX_FILE_SIZE) {
          this.attachedFiles = this.attachedFiles.filter(x => x.size <= MAX_FILE_SIZE)
          this.showWarningMessage(`The file "${x.name}" is too large. Please upload files smaller than 10 MB.`)
          return
        }
      })
      let completedFiles = e.filter(
        item => item.success || // files uploaded with success
          (!item.success && item.response && typeof item.response === 'string' && !this.isJson(item.response)) // files where requested failed
      )

      if (completedFiles.length === e.length) {
        this.uploadFinished = true
        this.$emit('uploadFinished', this.uploadFinished)

        let attachments = e.map((item) => {
          let uploadError = ''
          let itemResponse = null
          let clientDataPath = ''
          let mediaFile = ''

          if (item.success && item.response) {
            itemResponse = JSON.parse(item.response)
            if (itemResponse.success) {
              const regex = /(ClientData\/.*)/
              const match = itemResponse.path.match(regex)
              if (match && match.length > 1) {
                const filePath = match[1]
                clientDataPath = filePath.substring(filePath.indexOf('ClientData'))
                this.attachedPath.push(clientDataPath)
                const splits = clientDataPath.split('/')
                mediaFile = splits[splits.length - 1]
              } else {
                console.log('No matching files.')
              }
            }
            uploadError = (itemResponse && itemResponse.uploadError) ? itemResponse.uploadError : ''
          }
          if (!item.success) {
            uploadError = 'Upload request failed'
          }

          if (this.reconcileAction !== 'create' && !item.response) {
            const splits = item.path.split('/')
            mediaFile = splits[splits.length - 1]
          }

          const vendorId = this.$root.getUser.$.data.VendorID
          const VCI = this.$root.getUser.$.data.VCI

          if (uploadError) {
            this.showWarningMessage('File "' + item.name + '": ' + uploadError)
          } else {
            return {
              OriginalName: item.name,
              SizeBytes: item.size,
              MediaFile: item.mediaFile ? item.mediaFile : mediaFile || '',
              AttachmentFile: itemResponse ? itemResponse.file : (item.file || ''),
              Id: item.id ? item.id : '',
              PathWithName: item.pathWithName ? item.pathWithName : itemResponse ? clientDataPath : '',
              FullPath: item.path ? item.path : itemResponse.path ? itemResponse.path : '',
              ReadOnly: item.readonly || false,
              VendorId: vendorId,
              VCI: VCI
            }
          }
        })

        this.mediaFilesPath = this.attachedPath.join(', ')
        this.$emit('mediaFilesPathUpdated', this.mediaFilesPath)
        this.attachedPath = []

        try {
          this.attachedFiles = this.attachedFiles.filter(item =>
            item.success &&
            (
              (!item.response && item.name) || // take existing items from edit
              (JSON.parse(item.response).success) // take new added items
            )
          ).map(item => {
            if (item.response) {
              const response = JSON.parse(item.response)
              if (response.path) {
                item.path = response.path // assign the 'path' property from the response to the item
              }
            }
            return item
          })
        } catch (ee) {
          console.error(ee)
        }
        const filteredAttachments = attachments.filter(item => item)
        this.$emit('attachmentsUpdated', filteredAttachments)
        this.$emit('uploadFinished', this.uploadFinished)
        return filteredAttachments
      } else {
        this.uploadFinished = false
        this.$emit('uploadFinished', this.uploadFinished)
      }
    },

    isJson(str) {
      try {
        JSON.parse(str)
      } catch (e) {
        return false
      }
      return true
    },

    singularize(value) {
      return value.endsWith('s') ? value.slice(0, -1) : value
    },

    populateFiles() {
      if (this.transactionFiles.length > 0) {
        this.transactionFiles.forEach((file) => {
          var parts = file.OriginalName.split('.')
          var extension = ''
          var fileType = ''
          if (parts.length > 1) {
            extension = parts[parts.length - 1].toLowerCase()
          }
          switch (extension) {
            case 'png':
              fileType = 'image/png'
              break
            case 'pdf':
              fileType = 'application/pdf'
              break
            case 'jpg':
              fileType = 'image/jpg'
              break
            case 'jpeg':
              fileType = 'image/jpeg'
              break
            default:
              fileType = ''
          }
          this.attachedFiles.push({
            name: file.OriginalName,
            type: fileType,
            size: file.SizeBytes,
            file: file.MediaFile,
            mediaFile: file.MediaFile,
            path: file.FullPath,
            pathWithName: file.PathWithName,
            id: file.Id,
            success: true,
            readonly: this.reconcileAction === 'view'
          })
        })
        const attachments = this.attachedFiles.map(item => ({
          OriginalName: item.name,
          SizeBytes: item.size,
          MediaFile: item.mediaFile,
          AttachmentFile: item.file,
          PathWithName: item.pathWithName,
          FullPath: item.path,
          ReadOnly: item.readonly || false,
          Id: item.id
        }))
        const filteredAttachments = attachments.filter(item => item)
        this.$emit('attachmentsUpdated', filteredAttachments)
      }
    },

    setRowFieldsValues(fieldsValues) {
      if (this.loadedCategoriesAndBuAndCc === 0) {
        setTimeout(() => {
          this.setRowFieldsValues(fieldsValues)
        }, 100)
        return
      }
      this.loadedCategoriesAndBuAndCc = -1

      // No server-side coding rows to apply. Don't reset the per-row arrays
      // (descriptions/selectedCategory/selectedBu/selectedCc/referenceId/...) —
      // for a SUBMIT-match open, tryApplyReceiptScanPrefill already populated
      // them via applyAmountPrefill / applyLineItemsPrefill before this
      // deferred call lands, and the `= new Array(0).fill(...)` lines below
      // would clobber the prefill ('Subtotal' / 'Tax' descriptions disappear
      // about 100ms after the modal opens). For brand-new reconciles with no
      // prefill, the arrays start empty in data() so no-op is correct too.
      if (!Array.isArray(fieldsValues) || fieldsValues.length === 0) {
        this.codingRowsLoading = false
        return
      }

      var existingRow = this.form[0]

      while (this.form.length < fieldsValues.length) {
        const newRow = existingRow.map((field) => ({ Key: field.Key, Label: field.Label, Values: field.Values, Required: field.Required, Type: field.Type }))
        this.form.push(newRow)
        this.splitCounter++
      }

      if (this.hasBuAndCcPair) {
        this.selectedBuAndCcPair = new Array(fieldsValues.length).fill(null)
      }
      if (this.hasBu) {
        this.selectedBu = new Array(fieldsValues.length).fill(null)
      }
      if (this.hasCc) {
        this.selectedCc = new Array(fieldsValues.length).fill(null)
      }
      this.selectedCategory = new Array(fieldsValues.length).fill(null)
      this.predictedCategory = new Array(fieldsValues.length).fill(false)
      this.referenceId = new Array(fieldsValues.length).fill(null)
      this.descriptions = new Array(fieldsValues.length).fill('')

      fieldsValues.forEach((r, index) => {
        this.$set(this.form[index], 'Amount', r.Amount)
        this.form[index].ItemId = r.ItemId
        r.CustomFields.forEach(field => {
          this.form[index].forEach(f => {
            if (f.Key === field.FieldId) {
              if (f.Type === 'select') {
                const matchedOption = this.customOptions[f.Key].find(x => x.text === field.Value)
                if (matchedOption) {
                  this.$set(f, 'Values', matchedOption)
                }
              } else {
                f.Values = this.transaction && this.transaction.data && this.transaction.data.ReconcileId ? field.Value : f.Values
              }
            }
          })
        })

        if (this.hasBuAndCcPair) {
          if (r.ProgramBusinessUnitAndCostCenter && r.ProgramBusinessUnitAndCostCenter.Id) {
            this.selectedBuAndCcPair[index] = this.buAndCcPairs.find(pair => pair.programBusinessUnitAndCostCenterId === r.ProgramBusinessUnitAndCostCenter.Id.toString())
          }
        } else {
          if (r.BusinessUnit && r.BusinessUnit.Id) {
            this.selectedBu[index] = this.businessUnits.find(bu => bu.businessUnitId === r.BusinessUnit.Id.toString())
          }
          if (r.CostCenter && r.CostCenter.Id) {
            this.selectedCc[index] = this.costCenters.find(cc => cc.costCenterId === r.CostCenter.Id.toString())
          }
        }
        if (r.CategoryId) {
          this.selectedCategory[index] = this.categories.find(c => c.key === r.CategoryId.toString())
          this.referenceId[index] = r.ReferenceId
        }
        if (r.Description) {
          this.descriptions[index] = r.Description
        }
      })

      this.handleSplitAmounts()
      this.emitReconcileData()
      // Values are now in form/per-row arrays — drop the skeleton.
      this.codingRowsLoading = false

      // Existing reconcile values are now applied and the dropdown options are loaded,
      // so selectedCategory reflects the real coded/un-coded state. Attempt the silent prefill.
      this.prefillCategoryFromPrediction()
    },

    toggleSplit(index) {
      const existingRow = this.form[index]

      // PRODUCTDEV-5475 — initialize from the transaction amount, NOT 0. Splitting a $50 tx
      // should land on $50/$0, not $0/$0, so the user keeps the total in front of them to adjust;
      // it also stops a later "remove split" from snapping the single row back to the tx total
      // (handleSplitAmounts only resets to tx.Amount when the amount is missing/0). The null guard
      // preserves an already-typed amount, and prefill paths overwrite right after toggleSplit
      // returns, so subtotal+tax prefill is unaffected.
      if (this.form.length < 2 && this.form[index].Amount == null) {
        this.$set(this.form[index], 'Amount', this.transaction.data.Amount || 0)
      }

      // Clone the existing row fields
      const newRow = existingRow.map(field => ({
        Key: field.Key,
        Label: field.Label,
        Values: '', // Reset Values
        Required: field.Required,
        Type: field.Type
      }))

      this.baseCustomFields.forEach((baseObj, baseIndex) => {
        if (newRow[baseIndex]) {
          Object.keys(baseObj).forEach((key) => {
            newRow[baseIndex][key] = baseObj[key]
          })
        }
      })

      // Force reactivity
      this.$set(newRow, 'Amount', 0)

      // Add the new row in the form
      this.form.splice(index + 1, 0, newRow)
      if (this.hasBuAndCcPair) {
        this.selectedBuAndCcPair.splice(index + 1, 0, null)
      }
      if (this.hasBu) {
        this.selectedBu.splice(index + 1, 0, null)
      }
      if (this.hasCc) {
        this.selectedCc.splice(index + 1, 0, null)
      }
      this.selectedCategory.splice(index + 1, 0, null)
      if (!Array.isArray(this.predictedCategory)) {
        this.predictedCategory = new Array(this.form.length - 1).fill(false)
      }
      this.predictedCategory.splice(index + 1, 0, false)
      this.referenceId.splice(index + 1, 0, null)
      this.descriptions.splice(index + 1, 0, '')

      this.$nextTick(() => {
        this.handleSplitAmounts()
      })

      this.splitCounter++
      this.emitReconcileData()
    },

    removeSplit(index) {
      if (this.form.length > 1) {
        this.form.splice(index, 1)
        if (this.hasBuAndCcPair) {
          this.selectedBuAndCcPair.splice(index, 1)
        }
        if (this.hasBu) {
          this.selectedBu.splice(index, 1)
        }
        if (this.hasCc) {
          this.selectedCc.splice(index, 1)
        }
        this.selectedCategory.splice(index, 1)
        if (Array.isArray(this.predictedCategory)) {
          this.predictedCategory.splice(index, 1)
        }
        this.referenceId.splice(index, 1)
        this.descriptions.splice(index, 1)
        this.splitCounter--
        this.handleSplitAmounts()
        this.emitReconcileData()
        this.$forceUpdate()
      }
    },

    // Grow or shrink the form to exactly `target` rows so receipt-driven prefill
    // can apply cleanly regardless of the form's prior state. Used by the three
    // applyXxxPrefill paths when transitioning between prefill modes (e.g.
    // subtotal_and_tax → X → total_only would otherwise leave splitCounter=2
    // and the new total_only prefill would silently no-op).
    //
    // Tail rows are removed when shrinking — their per-row category/BU/CC/refId
    // metadata is intentionally discarded. This is fine because receipt-driven
    // prefill represents ground truth from the new receipt; the user hasn't
    // coded the new receipt's rows yet.
    normalizeFormForMode(target) {
      if (!this.form || target < 1) {
        return
      }
      while (this.form.length < target) {
        this.toggleSplit(this.form.length - 1)
      }
      while (this.form.length > target) {
        this.removeSplit(this.form.length - 1)
      }
    },

    copySplit(index) {
      if (this.form.length > 0) {
        this.copySplitObject.Amount = this.form[index].Amount
        this.copySplitObject.Fields = Array.from(this.form[index])
        if (this.hasBuAndCcPair) {
          this.copySplitObject.SelectedBuAndCcPair = this.selectedBuAndCcPair[index]
        }
        if (this.hasBu) {
          this.copySplitObject.SelectedBu = this.selectedBu[index]
        }
        if (this.hasCc) {
          this.copySplitObject.SelectedCc = this.selectedCc[index]
        }
        this.copySplitObject.SelectedCategory = this.selectedCategory[index]
        this.copySplitObject.ReferenceId = this.referenceId[index]
        this.copySplitObject.Description = this.descriptions[index]
      }
    },

    pasteSplit(index) {
      if (this.form.length > 0) {
        this.form[index].Amount = this.copySplitObject.Amount
        this.copySplitObject.Fields.forEach(field => {
          let matchingField = this.form[index].find(f => f.Key === field.Key)
          if (matchingField) {
            matchingField.Values = field.Values
          }
        })
        if (this.hasBuAndCcPair) {
          this.selectedBuAndCcPair[index] = this.copySplitObject.SelectedBuAndCcPair
        }
        if (this.hasBu) {
          this.selectedBu[index] = this.copySplitObject.SelectedBu
        }
        if (this.hasCc) {
          this.selectedCc[index] = this.copySplitObject.SelectedCc
        }
        this.selectedCategory[index] = this.copySplitObject.SelectedCategory
        this.$set(this.predictedCategory, index, false)
        this.referenceId[index] = this.copySplitObject.ReferenceId
        this.$set(this.descriptions, index, this.copySplitObject.Description != null ? this.copySplitObject.Description : '')
        this.handleSplitAmounts()
        this.emitReconcileData()
        this.$forceUpdate()
      }
    },

    resetSplit() {
      // Program change clears only program-scoped per-row state (BU / CC / BU+CC pair /
      // Category / ReferenceId) because those options come from the program's chart of
      // accounts and don't carry over. Split rows themselves stay — together with their
      // amounts and descriptions — because those are either receipt-OCR-derived
      // (Subtotal/Tax or line items from Photon) or user-typed, neither of which is
      // tied to the program.
      var rowCount = (this.form && this.form.length) ? this.form.length : 1
      this.selectedBu = new Array(rowCount).fill(null)
      this.selectedCc = new Array(rowCount).fill(null)
      this.selectedBuAndCcPair = new Array(rowCount).fill(null)
      this.selectedCategory = new Array(rowCount).fill(null)
      this.predictedCategory = new Array(rowCount).fill(false)
      this.referenceId = new Array(rowCount).fill(null)
      this.handleSplitAmounts()
      this.emitReconcileData()
      this.$forceUpdate()
    },

    formsTemplate() {
      if (typeof this.transaction === 'object' && this.transaction !== null) {
        if (!this.transaction.hasOwnProperty('form')) {
          this.transaction.form = JSON.parse(JSON.stringify(this.form))
        }
      }
    },

    async getCategoriesAndBuAndCc(programId) {
      this.loadedCategoriesAndBuAndCc = 0

      const response = await this.api.get(this.api.$.evedpaycard.categoriesAndBuAndCc, {
        params: {
          programId: programId,
          invoiceId: 0,
          loadAllData: true
        }
      })

      if (response && response.data) {
        if (response.data.costCentersAndBusinessUnit) {
          this.hasBuAndCcPair = response.data.costCentersAndBusinessUnit.areCombined || this.$root.getUser.settings.UseBusinessUnitAndCostCenterMapping
          if (this.hasBuAndCcPair) {
            if (response.data.costCentersAndBusinessUnit.mixedCostCentersAndBusinessUnits) {
              this.buAndCcPairs = response.data.costCentersAndBusinessUnit.mixedCostCentersAndBusinessUnits
            }
          } else {
            this.hasBu = response.data.costCentersAndBusinessUnit.businessUnits.length > 0 || this.$root.getUser.settings.HasBusinessUnit
            this.hasCc = response.data.costCentersAndBusinessUnit.costCenters.length > 0 || this.$root.getUser.settings.HasCostCenter
            if (this.hasBu) {
              this.businessUnits = response.data.costCentersAndBusinessUnit.businessUnits
            }
            if (this.hasCc) {
              this.costCenters = response.data.costCentersAndBusinessUnit.costCenters
            }
          }
        }
        if (response.data.categories) {
          this.categories = response.data.categories
        }

        // Per-division Predictive Coding toggle, carried on the existing categories response.
        // Consumed by the predict prefill; when falsy, no predict XHR is ever fired.
        this.allowPredictiveCoding = !!response.data.allowPredictiveCoding

        this.loadedCategoriesAndBuAndCc = 1
      }
    },

    onSelectProgramOpen() {
      this.programLoader.searchTerm = ''
      this.loadPrograms(true)
    },

    async loadPrograms(reset = false) {
      const loader = this.programLoader
      if (loader.isLoading) return

      if (reset) {
        loader.pageIndex = 0
        loader.items = []
        loader.hasMore = true
      }
      if (!loader.hasMore) return

      loader.isLoading = true
      try {
        const { data } = await this.api.get(
          this.api.$.evedpaycard.programs,
          {
            params: {
              divisionId: this.transaction.data.DivisionId,
              pageIndex: loader.pageIndex,
              pageSize: this.pageSize,
              searchTerm: loader.searchTerm
            }
          }
        )

        const { Items, HasMore } = data

        const mapped = Items.map(p => ({
          Id: p.ProgramId,
          Name: p.ProgramName,
          InternalProgramID: p.InternalProgramID
        }))

        loader.items = reset
          ? mapped
          : loader.items.concat(mapped)
        loader.hasMore = HasMore
        loader.pageIndex += 1

        this.programOptions = loader.items
      } catch (e) {
        console.error('loadPrograms error', e)
      } finally {
        loader.isLoading = false
      }
    },

    onProgramSearch(term) {
      this.programLoader.searchTerm = term
      this.loadPrograms(true)
    },

    async getReconcile(row) {
      this.programs = []
      // Guarantee codingRowsLoading flips false on EVERY exit — without this,
      // any failure between here and setRowFieldsValues (network blip, 500,
      // transient perm error, getCustomOptions rejection) leaves the coding
      // <tbody> stuck behind the shimmer with no recovery short of closing
      // the modal.
      let reachedSetRowFieldsValues = false
      try {
        const response = await this.api.get('evedpaycards/transactions/get-reconcile', {
          params: {
            transactionId: row.Id,
            divisionId: row.DivisionId
          }
        })

        if (response && response.data) {
          this.noteText = response.data.ReconcileNote
          this.requireReceiptDescription = response.data.RequireReceiptDescription
          this.handleTextDescription()

          this.transactionFiles = response.data.TransactionFiles
          this.populateFiles()
          // PRODUCTDEV-5772 — hydrate supporting documents (separate from receipts).
          this.populateAdditionalDocuments(response.data.AdditionalDocuments)

          const form = response.data.Form
          this.formKey = form.Key
          if (form.Fieldsets && form.Fieldsets.length > 0) {
            this.form = [form.Fieldsets[0].Fields]
            this.baseCustomFields = JSON.parse(JSON.stringify(form.Fieldsets[0].Fields))
          } else {
            this.form = [[]]
            this.baseCustomFields = [[]]
          }
          this.$emit('fields-updated', { fields: this.form, formKey: this.formKey })
          this.formsTemplate()

          for (const x of this.form[0]) {
            if (x.Type === 'select') {
              this.$set(this.customOptions, x.Key, [])
              if (x.DataSource && x.DataSource.Url) {
                await this.getCustomOptions(x.DataSource.Url, x.Key)
              }
            }
          }

          // PRODUCTDEV-5772 — always load whatever coding was saved (the most recent
          // save) and show it on reopen. A previous "orphan-rescue" guard discarded
          // saved coding here for Open txns that once had a receipt but no longer do —
          // that was a band-aid for the old remove-receipt-clears-coding behavior, which
          // no longer exists. Coding now persists by design (✕ / replace never wipe it),
          // so the form must reflect exactly what was saved last.
          var transactionFields = response.data.TransactionFields
          // Snapshot "opened with coding" synchronously (before setRowFieldsValues'
          // deferred per-row population) so hasCodingOrReceipt is correct immediately.
          this.loadedWithCoding = Array.isArray(transactionFields) && transactionFields.length > 0
          reachedSetRowFieldsValues = true
          this.setRowFieldsValues(transactionFields)

          if (Array.isArray(response.data.Descriptions)) {
            this.descriptions = response.data.Descriptions
          }
        }
      } finally {
        // setRowFieldsValues already flips this flag on its own success
        // path; the finally only matters on the error path where we never
        // reached it. Without the guard the loading shimmer would mask
        // the coding rows for the rest of the modal's lifetime.
        if (!reachedSetRowFieldsValues) {
          this.codingRowsLoading = false
        }
      }
    },

    fetchSuppliersOnOpen() {
      this.suppliers.length || this.api.get(this.api.$.evedpaycard.suppliers, {
        params: {
          q: ''
        }
      })
        .then(r => {
          if (r && r.data) {
            this.supplierOptions = r.data
            this.originalSuppliers = r.data
          }
        })
        .catch(e => console.warn(e))
    },

    async fetchSuppliers(query) {
      let results = await this.api.get(this.api.$.evedpaycard.suppliers, {
        params: {
          q: query || ''
        }
      })

      if (results && results.data) {
        this.supplierOptions = results.data
      }
    },

    getSelectedSupplier(e) {
      this.selectedSupplier = e.Value.VendorId
    },

    getLabel(option) {
      if (Object.keys(option).length > 0) {
        return option.Value.CompanyName + (option.Value.Vci ? ' (#' + option.Value.Vci + ')' : '')
      }
    },

    handleSplitAmounts() {
      this.form.forEach((item, index) => {
        // Preserve an explicitly-blank Amount (null / undefined). Receipt prefill in
        // subtotal_and_tax mode uses null to mean "Photon couldn't extract this side —
        // let the user type it in" — coercing it back to 0 would render $0.00 in the
        // input and mask the intent.
        if (item.Amount === null || item.Amount === undefined) {
          return
        }

        // PRODUCTDEV-5475 — keep a row's amount whenever it's a real value. A 0 only
        // counts as "real" when the row carries a description (a prefilled "Subtotal"/
        // "Tax" line, or one the user labelled), so a no-tax receipt's $0 row survives a
        // split removal. A 0/blank on an UNdescribed row is just a placeholder: a single
        // row falls back to the transaction total, a multi-row stays 0. Non-zero amounts
        // are always kept regardless of description.
        const hasDescription = !!(this.descriptions && String(this.descriptions[index] || '').trim())
        const amountNum = parseFloat(item.Amount)
        const keepAmount = item.Amount !== '' && !isNaN(amountNum) && (amountNum !== 0 || hasDescription)
        const parsedAmount = keepAmount
          ? amountNum
          : (this.form.length === 1 ? parseFloat(this.transaction.data.Amount) : 0)

        // Ensure the amount is set reactively
        if (!isNaN(parsedAmount)) {
          this.$set(item, 'Amount', parsedAmount)
        }
      })

      // Make sure the split amounts are updated and Vue re-renders
      this.splitAmounts = this.form.map(item => item.Amount)
      this.$forceUpdate()  // Force Vue to update the DOM
      this.$emit('split-amounts', this.splitAmounts)
    },

    handleTextDescription() {
      const data = {
        noteText: this.noteText,
        requireReceiptDescription: this.requireReceiptDescription
      }

      this.$emit('description-text', data)
      this.fieldsIncomplete.descriptionIncomplete = false
    },

    handleSupplierSelection(option) {
      this.selectedSupplier = option
      this.emitReconcileData()
    },

    async handleProgramChange(option) {
      this.selectedProgram = option !== null ? option : Object
      this.resetSplit()
      if (option && option.Id) {
        await this.getCategoriesAndBuAndCc(option.Id)
        // PRODUCTDEV-5699 — the dominant flow opens the transaction with no program and
        // picks it here. The load-path prefill (setRowFieldsValues) only runs for a
        // transaction that already carried a program at open, so without this re-fire no
        // prediction ever runs for an interactively chosen program. getCategoriesAndBuAndCc
        // has just refreshed allowPredictiveCoding for THIS program's division and resetSplit
        // cleared the row, so the reactive precondition holds (division on, categories loaded,
        // merchant known, un-coded row). The division / categories-loaded / no-clobber gates
        // all live inside prefillCategoryFromPrediction, so re-calling it stays gated.
        this.prefillCategoryFromPrediction()
      }
      this.emitReconcileData()
    },

    handleProgramInput(option) {
      if (this.previousSelectedProgram && option && this.previousSelectedProgram.Id === option.Id) {
        this.handleProgramChange(option)
        return
      }

      this.programOption = option
      if (Object.keys(this.previousSelectedProgram).length !== 0 && this.previousSelectedProgram && this.previousSelectedProgram.Id && this.previousSelectedProgram.Name) {
        this.showAlert = true
      } else {
        this.handleProgramChange(option)
      }
    },

    handleBUSelection(option, rowIndex) {
      this.selectedBu[rowIndex] = option
      this.emitReconcileData()
    },

    handleCCSelection(option, rowIndex) {
      this.selectedCc[rowIndex] = option
      this.emitReconcileData()
    },

    handleBuAndCcPairSelection(option, rowIndex) {
      this.selectedBuAndCcPair[rowIndex] = option
      this.emitReconcileData()
    },

    handleCategorySelection(option, rowIndex, opts) {
      // PRODUCTDEV-5699 — use $set, not direct array-index assignment. Vue 2 does not track
      // `this.arr[i] = v`, so a programmatic prefill (predictive coding) set the data but the
      // bound <ed-select> never re-rendered — the predicted Category looked unselected. Manual
      // picks worked because they flow through the component's own v-model. $set makes the
      // programmatic set reactive for both paths.
      this.$set(this.selectedCategory, rowIndex, option)
      this.$set(this.referenceId, rowIndex, option && option.referenceId ? option.referenceId : null)
      this.$set(this.predictedCategory, rowIndex, !!(this.allowPredictiveCoding && opts && opts.predicted && option))
      this.emitReconcileData()
    },

    // Predictive Coding: silent, fail-silent Category prefill for an un-coded EPC reconcile row.
    // Fire-and-forget; any failure leaves the field empty and the user codes manually.
    async prefillCategoryFromPrediction(rowIndex = 0) {
      // Division gate: never fire the XHR when Predictive Coding is off for this division.
      if (!this.allowPredictiveCoding) return
      // The dropdown options must be loaded, else the returned categoryId cannot be matched.
      if (!this.categories || this.categories.length === 0) return
      // Skip rows that already have a Category (no "Other" default on EPC, so any value means coded).
      if (this.selectedCategory[rowIndex]) return

      const row = this.transaction && this.transaction.data
      if (!row) return

      // PRODUCTDEV-5699 — use the program the user actually has selected. In the dominant flow
      // the program is picked after the transaction opens, so transaction.data.ProgramId is still
      // null/stale here; fall back to it for the load path where the two are equal.
      const programId = (this.selectedProgram && this.selectedProgram.Id) || row.ProgramId
      if (!programId) return

      try {
        const response = await this.api.platform.post(this.api.$.predictiveCoding.predict, {
          programId: programId,
          divisionId: row.DivisionId,
          surface: EPC_RECONCILE_SURFACE,
          supplierId: null,
          merchantName: row.Merchant,
          itemName: null,
          description: null,
          amount: row.Amount != null ? row.Amount : null,
          mcc: null
        }, { timeout: PREDICT_TIMEOUT_MS })

        const prediction = response && response.data
        // null categoryId means no prediction (miss/short-circuit): leave the field empty.
        if (!prediction || prediction.categoryId == null) return
        // Never overwrite a value the user touched while the prediction was in flight.
        if (this.selectedCategory[rowIndex]) return

        const option = this.findPredictedCategoryOption(prediction.categoryId, prediction.subCategoryId)
        if (option) {
          // Reuse the manual-pick handler so Reference Id fills from option.referenceId exactly as on a manual pick.
          this.handleCategorySelection(option, rowIndex, { predicted: true })
        }
      } catch (e) {
        // Fail-silent: swallow any network error / timeout / non-200 / malformed body. No toast, no rethrow.
      }
    },

    // Match a predicted categoryId/subCategoryId pair to a dropdown option by its composite key.
    // Options carry key = `${categoryId}_${subCategoryId}` when a subcategory is present, else `${categoryId}`.
    findPredictedCategoryOption(categoryId, subCategoryId) {
      const key = subCategoryId != null ? `${categoryId}_${subCategoryId}` : `${categoryId}`
      return this.categories.find(option => option.key === key)
    },

    handleReferenceID(value, rowIndex) {
      this.referenceId[rowIndex] = value
      this.emitReconcileData()
    },

    handleDescription(value, rowIndex) {
      // Mirrors handleReferenceID — prefill seeds the description ('Total' /
      // 'Subtotal' / 'Tax' / line-item name), the user can correct it inline,
      // and emitReconcileData carries the edits to the save payload via
      // form.Descriptions[i].
      this.descriptions[rowIndex] = value
      this.emitReconcileData()
    },

    emitReconcileData() {
      const data = {
        itemIds: this.form.map(item => item.ItemId),
        splitCounter: this.splitCounter,
        selectedSupplier: this.selectedSupplier !== null ? this.selectedSupplier : Object,
        selectedProgram: this.selectedProgram !== null ? this.selectedProgram : Object,
        selectedCategory: this.selectedCategory.map(x => x && x.key),
        referenceId: this.referenceId !== null ? this.referenceId : '',
        descriptions: this.descriptions,
        fields: this.form
      }
      if (this.hasBuAndCcPair) {
        data.selectedBu = this.selectedBuAndCcPair.map(x => x && parseInt(x.businessUnitId, 10))
        data.selectedCc = this.selectedBuAndCcPair.map(x => x && parseInt(x.costCenterId, 10))
        data.selectedBuAndCcPair = this.selectedBuAndCcPair.map(x => x && parseInt(x.programBusinessUnitAndCostCenterId, 10))
      } else {
        if (this.hasBu) {
          data.selectedBu = this.selectedBu.map(x => x && parseInt(x.businessUnitId, 10))
        }
        if (this.hasCc) {
          data.selectedCc = this.selectedCc.map(x => x && parseInt(x.costCenterId, 10))
        }
      }

      this.$emit('reconcile-created', data)
    },

    formatDate: function (date) {
      const momentDateFormat = this.$root.getUser.sessionSettings.DateAndTimeFormats.MomentDateFormat
      return global.moment(date).format(momentDateFormat)
    },

    showWarningMessage(message) {
      this.$nextTick(() => {
        this.$notifications.notify({
          type: 'plain',
          icon: 'exclamation-circle',
          placement: { from: 'top', align: 'center' },
          timeout: 0,
          message: message,
          discard: 'Ok',
          goto: ''
        })
      })
    },

    // Pick up the Legacy /UploadReceiptReconcile metadata that Card-list's
    // Dashboard SUBMIT flow stored before navigation, push it to attachedFiles
    // so the save payload has a TransactionFile row, then delete the key.
    // No-op when called without a stash entry (e.g. plain reconcile reopen).
    consumeStashedLegacyReceipt (receiptScanId) {
      if (!receiptScanId) return
      var key = 'receiptScanLegacyPath_' + receiptScanId
      var raw
      try {
        raw = global.sessionStorage.getItem(key)
      } catch (err) {
        return
      }
      if (!raw) return
      var meta
      try {
        meta = JSON.parse(raw)
      } catch (err) {
        try { global.sessionStorage.removeItem(key) } catch (e) {}
        return
      }
      try { global.sessionStorage.removeItem(key) } catch (e) {}
      if (!meta || !meta.pathWithName) return
      // Don't double-attach if populateFiles already pulled an existing
      // TransactionFile for the same path (e.g. on a refresh after save).
      var alreadyAttached = this.attachedFiles.some(function (f) {
        return f && f.pathWithName === meta.pathWithName
      })
      if (alreadyAttached) return
      this.attachedFiles.push({
        name: meta.name || 'Receipt',
        type: meta.type || '',
        size: meta.size || 0,
        file: meta.file || meta.mediaFile || '',
        mediaFile: meta.mediaFile || meta.file || '',
        path: meta.path || '',
        pathWithName: meta.pathWithName,
        id: '',
        success: true,
        readonly: false,
        receiptScanId: receiptScanId,
        // Flag: this attachment came from a pre-existing matched scan (uploaded
        // before the modal opened, via Dashboard SUBMIT or the folder picker).
        // cleanupUnsavedReceipts must skip it on cancel — the scan is already in
        // Matched state, and DELETE on it would orphan the match + (per Tudor's
        // repro) the BE request hangs since the scan isn't a fresh upload.
        preExistingMatch: true
      })
      this.emitAttachmentsUpdated()
    },

    // Mobile FAB flow bridge (PRODUCTDEV-5475): a scan matched on the SubmitReceipt
    // page is Platform-only — Legacy has no TransactionFiles row yet, so populateFiles
    // leaves the Receipt slot empty. Fetch the scan's FileInfo + presigned URL and push
    // a synthetic attachment so hasAttachment flips true and the receipt renders.
    // Called only when isMobile && attachedFiles is empty (see init) — desktop never
    // arrives in this state.
    async attachReceiptScanIfMissing (receiptScanId) {
      if (!receiptScanId) return
      try {
        var results = await Promise.all([
          receiptScansApi.getStatus(receiptScanId),
          receiptScansApi.getImage(receiptScanId).catch(function () { return null })
        ])
        var scan = results[0]
        var imageRes = results[1]
        var fi = scan && scan.fileInfo
        if (!fi || !fi.providerKey) {
          // Older scans without FileInfo — bail silently; user can still attach
          // manually via the camera/upload buttons.
          return
        }
        var presignedUrl = ''
        if (imageRes && typeof imageRes === 'object') {
          presignedUrl = imageRes.presignedUrl || imageRes.url ||
            (imageRes.data && (imageRes.data.presignedUrl || imageRes.data.url)) || ''
        } else if (typeof imageRes === 'string') {
          presignedUrl = imageRes
        }
        var name = fi.originalFileName || 'receipt'
        var parts = name.split('.')
        var ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
        var fileType = ''
        switch (ext) {
          case 'png': fileType = 'image/png'; break
          case 'pdf': fileType = 'application/pdf'; break
          case 'jpg': fileType = 'image/jpg'; break
          case 'jpeg': fileType = 'image/jpeg'; break
          default: fileType = fi.contentType || ''
        }
        // path = presigned URL drives <img src> via the computed previewUrl.
        // pathWithName = the scan image's durable STAGED S3 key (PRODUCTDEV-5783
        // pattern), derived from the presigned URL exactly like the folder
        // claim/replace flows — Legacy persists it and reopen re-serves it as
        // AWS_ASSETS_PRIVATE_PATH + PathWithName through the session-authed
        // Storage/private proxy. Do NOT store fi.providerKey here: that is
        // Photon's RESULT ARTIFACT key (data/…/*.json), which the proxy refuses
        // (no auth case for 'data/…') — every FAB-matched reconcile reopened
        // with a broken receipt image until 2026-06-06. providerKey survives
        // only as a non-empty fallback so the save payload never carries an
        // empty PathWithName (empty crashes Legacy's reopen path).
        var stagedKey = this.stagedKeyFromPresignedUrl(presignedUrl)
        this.attachedFiles.push({
          name: name,
          type: fileType,
          size: fi.sizeBytes,
          file: name,
          mediaFile: name,
          path: presignedUrl || fi.providerKey,
          pathWithName: stagedKey || fi.providerKey,
          id: '',
          success: true,
          readonly: false,
          receiptScanId: receiptScanId,
          // Scan is already in Matched state from SubmitReceipt's matchToTransaction.
          // cleanupUnsavedReceipts must skip it on cancel — DELETE on a Matched
          // scan orphans the link (same constraint as the folder-pick
          // preExistingMatch flag).
          preExistingMatch: true
        })
        this.emitAttachmentsUpdated()
      } catch (err) {
        // Non-critical — user can still manually attach via the camera/upload buttons.
        console.warn('attachReceiptScanIfMissing failed:', err)
      }
    },

    async tryApplyReceiptScanPrefill(receiptScanIdArg) {
      const receiptScanId = receiptScanIdArg || this.$route.query.receiptScanId

      // PRODUCTDEV-5788 — never scan/prefill when the division is "No Receipt Capture".
      if (!receiptScanId || !this.receiptScanEnabled || this.receiptCaptureDisabled) {
        return
      }

      // Check for pre-fetched prefill (Card-list.openMatchedReconcile pre-calls
      // /prefill before opening the modal so the form arrives populated). One-shot:
      // remove the key after consuming so a refresh doesn't reuse stale data.
      let prefillData = null
      let prefillDeferred = false
      try {
        const stashKey = 'receiptScanPrefill_' + receiptScanId
        const stashed = global.sessionStorage.getItem(stashKey)
        if (stashed) {
          try {
            prefillData = JSON.parse(stashed)
          } catch (e) {
            prefillData = null
          }
          try { global.sessionStorage.removeItem(stashKey) } catch (e) {}
        }
      } catch (e) {
        // sessionStorage disabled / SSR — fall through to API
      }

      try {
        if (!prefillData) {
          // PRODUCTDEV-5805 — pass the reconcile division so a folder-picked (unmatched) receipt
          // prefills to this division's selected mode, not the BE default.
          prefillData = await receiptScansApi.getPrefill(receiptScanId, this.receiptScanDivisionId)
        }

        if (prefillData) {
          // ReceiptPrefillDto shape: { amount: { total, subtotal, tax, currencyCode }, lineItems,
          //   overallConfidence, prefillMode, fallbackReason, warnings, merchantName, transactionDate, description }
          var amount = prefillData.amount || {}

          if (prefillData.prefillMode === 'line_items_and_tax' &&
              Array.isArray(prefillData.lineItems) &&
              prefillData.lineItems.length > 0) {
            this.applyLineItemsPrefill(prefillData.lineItems)
          } else {
            this.applyAmountPrefill({
              totalAmount: amount.total,
              subtotalAmount: amount.subtotal,
              taxAmount: amount.tax
            })
          }

          this.receiptScanPrefillApplied = true
          this.receiptScanPrefillMode = prefillData.prefillMode || null
          this.receiptScanConfidence = prefillData.overallConfidence != null
            ? (prefillData.overallConfidence < 0.7 ? 'low' : (prefillData.overallConfidence < 0.9 ? 'medium' : 'high'))
            : null
          // Capture the mismatch signal + Photon's total so the banner can show
          // both amounts. `amountMatch` is the BE-side comparison code; the
          // warnings array is the canonical user-facing trigger.
          this.amountMismatch = Array.isArray(prefillData.warnings) &&
            prefillData.warnings.indexOf('warning.amount_mismatch') >= 0
          this.receiptScanPhotonTotal = amount && amount.total != null ? amount.total : null
        }
      } catch (error) {
        // Suppress the toast when the scan simply isn't ready yet. The inline
        // call from uploadNewReceipt fires in parallel with the poller, so it
        // commonly hits /prefill before Photon has finished — Platform returns
        // 409 (not_ready); 404 covers the rare race where the scan was disposed
        // before this call landed; sentinel errors (timeout / 5xx) include the
        // 15s pollInstance timeout that fires when Photon takes longer than
        // expected. In all three the watcher-driven prefill on
        // `receiptScan.status` still runs once it flips to Complete, so the user isn't
        // left without prefill — surfacing a warning is just noise.
        var status = error && error.response && error.response.status
        var notReady = status === 409 || status === 404 || error.isPlatformSentinelError
        if (notReady) {
          prefillDeferred = true
          console.warn('Receipt prefill deferred to poller path:', status || (error && error.message))
        } else {
          console.error('Receipt scan prefill error:', error)
          this.showWarningMessage('Could not load receipt prefill data. Proceeding without prefill.')
        }
      } finally {
        // Photon round-trip is over (success OR non-retry error) — clear the
        // skeleton and lower the gate so the bar's post-100% tick in
        // startUploadProgressBar doesn't re-engage it. EXCEPTION: when the
        // inline /prefill returned 409/404/sentinel "not_ready", the
        // poller-driven `receiptScan.status` watcher is still going to apply
        // prefill on Complete. Leaving scanCompleted
        // false here keeps the bar-post-100% tick eligible to flip the
        // skeleton ON, covering the bar-end → Photon-complete gap (~10s for
        // typical Photon timing). The watcher clears both flags when it lands.
        if (!prefillDeferred) {
          this.scanCompleted = true
          this.scanningReceipt = false
        }
      }
    },

    applyAmountPrefill(prefillData) {
      if (!prefillData || this.reconcileAction === 'view') {
        return
      }

      var hasSubtotalAndTax = prefillData.subtotalAmount != null && prefillData.taxAmount != null
      if (hasSubtotalAndTax && this.form) {
        // Normalize to exactly 2 rows — handles fresh state (split from 1→2) AND
        // the receipt-replacement case where the user just X'd a prior receipt
        // that left the form at splitCounter=N (e.g. line_items_and_tax).
        this.normalizeFormForMode(2)
        if (!this.form[0]) {
          return
        }

        // Photon /api/v4 emits 0 (not null) for Subtotal/Tax when it failed to extract them
        // — confidence==0 in the raw JSON. Both-zero against any total is the unambiguous
        // "no breakdown" signal. In that case leave the Amount fields BLANK so the user
        // can type the split themselves rather than overwriting the form with $0.00s.
        // When at least one side is non-zero we trust Photon's numbers (legit no-tax
        // receipts have subtotal>0, tax=0 with high confidence and stay correct).
        var bothZero = prefillData.subtotalAmount === 0 && prefillData.taxAmount === 0
        this.$set(this.form[0], 'Amount', bothZero ? null : prefillData.subtotalAmount)

        if (this.form[1]) {
          this.$set(this.form[1], 'Amount', bothZero ? null : prefillData.taxAmount)
        }

        // Hardcoded row labels — the OCR doesn't tag subtotal/tax as line items,
        // so we synthesize the two labels that match what the user sees in the Receipt
        // Data panel. Saved to Legacy via form.Descriptions[i] on submit.
        this.descriptions = ['Subtotal', 'Tax']

        this.handleSplitAmounts()
        // handleSplitAmounts only emits 'split-amounts'; descriptions ride on
        // reconcile-created, so emit that too or the labels never reach the save payload.
        this.emitReconcileData()
      } else if (prefillData.totalAmount != null && this.form) {
        // Normalize to exactly 1 row — handles fresh state AND the
        // receipt-replacement case where the prior receipt left the form at
        // splitCounter > 1 (the bug Tudor reported: sub+tax → X → total_only
        // used to silently no-op because of a `splitCounter === 1` guard here).
        this.normalizeFormForMode(1)
        var row = this.form[0]
        if (row) {
          this.$set(row, 'Amount', prefillData.totalAmount)
          // Symmetric with the subtotal_and_tax branch above — give the single
          // row a label so the user has a visible "prefill happened" signal in
          // the Description column.
          this.descriptions = ['Total']
          this.handleSplitAmounts()
          this.emitReconcileData()
        }
      }
    },

    applyLineItemsPrefill (lineItems) {
      if (!Array.isArray(lineItems) || lineItems.length === 0 ||
          this.reconcileAction === 'view' ||
          !this.form) {
        return
      }

      // Normalize to exactly N rows. Used to require splitCounter===1 and grew
      // the form with a loop of toggleSplit; that broke the receipt-replacement
      // case (prior receipt left the form with the wrong row count and the
      // function silently no-op'd). normalizeFormForMode handles both grow and
      // shrink uniformly.
      this.normalizeFormForMode(lineItems.length)

      for (var j = 0; j < lineItems.length; j++) {
        if (this.form[j]) {
          this.$set(this.form[j], 'Amount', lineItems[j].amount)
        }
      }

      // `descriptions` — per-row split-item labels. Read back from Legacy split-item
      // Description column (EvedPayCardsRepository:2984) and sent on save via indexed
      // parallel array (form.Descriptions[i]). Falsy entries render no span — see Coding row template.
      this.descriptions = lineItems.map(li => (li && li.description) || '')

      this.handleSplitAmounts()
      // Push descriptions up to the parent so they ride the save payload —
      // handleSplitAmounts only emits 'split-amounts'.
      this.emitReconcileData()
    },

    openFolderPicker () {
      this.showFolderPicker = true
    },

    closeFolderPicker () {
      this.showFolderPicker = false
      this.folderPickerIsProcessing = false
    },

    async handleFolderPickerSelect (event) {
      // PRODUCTDEV-5772 — coding exists or a receipt is attached: replacing from the
      // folder swaps the file only (no claim, no prefill), then surfaces the
      // "Replace coding?" alert. Only a fresh/default reconcile claims + auto-prefills.
      if (this.hasCodingOrReceipt) {
        return this.replaceReceiptFromFolder(event)
      }
      var receiptScanId = event && event.receiptScanId
      if (!receiptScanId || this.folderPickerIsProcessing) {
        return
      }
      this.folderPickerIsProcessing = true
      // Cover the form's stale Amount + description cells with the skeleton
      // for the whole claim + prefill window — folder-pick has no progress
      // bar, so without this the user sees the prior receipt's values until
      // the new prefill arrives ~1-3s later. Cleared by tryApplyReceiptScanPrefill's
      // finally (success / non-retry error) or by the receiptScan.status
      // watcher on a terminal status.
      this.scanCompleted = false
      this.scanningReceipt = true
      // PRODUCTDEV-5802 (reopen) — close the picker right away and run the whole
      // fetch/upload window in the reconcile modal itself: file chip visible
      // immediately, old-UI spinner over the Receipt section, Save gated. This is
      // the same affordance Case 1 (Upload new receipt) shows, per the ticket.
      this.closeFolderPicker()
      this.receiptUploadInProgress = true
      this.acquireSaveGate()
      var self = this
      // Optimistic chip from the folder row's own metadata so the FILE is visible
      // under the spinner from the first frame. Enriched below (preview path,
      // staged key, Legacy path) as each piece resolves; rolled back on failure.
      var optimisticAttachment = {
        name: (event && event.originalFileName) || 'Receipt',
        type: (event && event.contentType) || '',
        size: (event && event.sizeBytes != null) ? event.sizeBytes : 0,
        file: '',
        mediaFile: '',
        path: '',
        pathWithName: '',
        id: '',
        success: true,
        readonly: false,
        receiptScanId: receiptScanId,
        _blobUrl: '',
        // PRODUCTDEV-5783 — the folder receipt is NOT claimed at pick time. It stays
        // FolderSaved (in the folder) until the reconcile is SAVED; claimPendingFolderReceipts()
        // performs the FolderSaved → Matched transition on save success. On cancel the scan is
        // left untouched (cleanupUnsavedReceipts skips it) so it remains in the folder, and an
        // explicit ✕ removal also leaves it in the folder (removePrimaryAttachment skips it).
        pendingFolderClaim: true
      }
      self.attachedFiles.push(optimisticAttachment)
      self.emitAttachmentsUpdated()
      try {
        var statusData = await receiptScansApi.getStatus(receiptScanId)
        if (!statusData || statusData.status !== 'FolderSaved') {
          self.$notifications.notify({
            type: 'warning',
            message: 'Receipt is no longer in the folder — pick another',
            placement: { from: 'top', align: 'center' }
          })
          self.rollbackFolderPickAttachment(optimisticAttachment)
          return
        }

        // Option D — Legacy-first dual-write so PathWithName is populated
        // on the Legacy TransactionFiles row. Without this, reopen-view crashes
        // via Substring(0, -1) on an empty path and the receipt is invisible
        // from a saved reconcile. Mirrors uploadNewReceipt's pattern (1656-1707).
        //
        // The folder receipt's bytes live in Platform's S3; we fetch via the
        // presigned URL, push to Legacy, and use Legacy's ClientData/... path
        // as the canonical pathWithName. The Platform side already has the
        // scan — claimReceipt is the existing FolderSaved → Matched transition.
        var imgRes
        try {
          imgRes = await receiptScansApi.getImage(receiptScanId)
        } catch (imgErr) {
          console.warn('Could not fetch folder receipt image:', imgErr)
        }

        var presignedUrl = imgRes && imgRes.presignedUrl
        // PRODUCTDEV-5783 — durable reopen key (see stagedKeyFromPresignedUrl): stored as PathWithName
        // so Legacy's Storage/private proxy re-serves the receipt on reopen even when the S3 byte-fetch
        // below is CORS-blocked and no permanent Legacy copy gets made.
        var stagedKey = self.stagedKeyFromPresignedUrl(presignedUrl)
        // Prefer the folder row's own metadata (passed from the picker) for name/type/size —
        // it's authoritative and present even when the S3 byte-fetch below is blocked, which is
        // what left the chip showing 0 bytes (PRODUCTDEV-5783). getImage (GetReceiptImageResult)
        // only returns fileName/contentType, never size, so size in particular must come from the row.
        var fileName = (event && event.originalFileName) || (imgRes && imgRes.fileName) || 'Receipt'
        var contentType = (event && event.contentType) || (imgRes && imgRes.contentType) || ''

        // Fetch the bytes ONCE — used for both the optimistic preview (via
        // local blob URL, same render path as uploadNewReceipt at :1755) and
        // the Legacy upload below. Without the blob URL preview the receipt
        // panel renders empty: ReceiptImageViewer reads attachment.path, and
        // an S3 presigned URL stuck directly in there isn't reliably
        // loadable inside the modal's render context.
        var blob = null
        var blobUrl = ''
        if (presignedUrl) {
          try {
            var imgFetch = await fetch(presignedUrl)
            if (imgFetch && imgFetch.ok) {
              blob = await imgFetch.blob()
              blobUrl = URL.createObjectURL(blob)
              contentType = blob.type || contentType
            }
          } catch (fetchErr) {
            console.warn('Could not fetch folder receipt bytes:', fetchErr)
          }
        }
        // Real size from the folder row; fall back to the fetched blob only if the row didn't
        // carry one. (Previously this was blob.size-or-0, so a blocked fetch showed 0 bytes.)
        var fileSize = (event && event.sizeBytes != null) ? event.sizeBytes : (blob ? blob.size : 0)

        // Enrich the early chip with what's now resolved: authoritative
        // name/type (getImage fallback), the preview path — local blob URL when
        // the fetch succeeded (fastest render), else the presigned S3 URL
        // (<img src> loads cross-origin without CORS) — and the durable staged
        // key (PRODUCTDEV-5783) so reopen works via the Storage/private proxy;
        // the Legacy dual-upload below overwrites it with the permanent
        // (non-staged) ClientData path when the byte-fetch succeeded.
        optimisticAttachment.name = fileName
        optimisticAttachment.type = contentType
        optimisticAttachment.size = fileSize
        optimisticAttachment.path = blobUrl || presignedUrl || ''
        optimisticAttachment.pathWithName = stagedKey
        // Track the blob URL separately so removePrimaryAttachment / beforeDestroy
        // can revoke it. `path` may be swapped to a non-blob URL later, so the
        // raw blob handle has to live in its own field.
        optimisticAttachment._blobUrl = blobUrl || ''
        self.emitAttachmentsUpdated()

        // Legacy dual-upload — reuse the blob we already fetched. Without
        // this the Legacy TransactionFiles row has PathWithName='' and
        // reopen-view crashes (Substring(0,-1)).
        if (blob) {
          var legacyUrl = self.api && self.api.env ? self.api.env + self.api.$.evedpaycard.receipt : null
          if (legacyUrl) {
            try {
              var blobType = blob.type || contentType || 'application/octet-stream'
              var legacyFile = new File([blob], fileName, { type: blobType })
              var legacyFormData = new FormData()
              legacyFormData.append('attachment_upload', legacyFile)
              var legacyToken = self.$user && self.$user.sessionSettings ? self.$user.sessionSettings.Token : ''
              var legacyResp = await self.api.post(legacyUrl, legacyFormData, {
                headers: { 'Authorization': 'Bearer ' + legacyToken, 'Content-Type': 'multipart/form-data' }
              })
              var legacyBody = legacyResp && legacyResp.data
              if (typeof legacyBody === 'string') {
                try { legacyBody = JSON.parse(legacyBody) } catch (e) { legacyBody = null }
              }
              if (legacyBody && legacyBody.success && legacyBody.path) {
                var pwnMatch = legacyBody.path.match(/(ClientData\/.*)/)
                var clientDataPath = pwnMatch && pwnMatch[1] ? pwnMatch[1] : legacyBody.path
                var mediaFile = legacyBody.file || clientDataPath.split('/').pop()
                // PRODUCTDEV-5783 — overwrite path with Legacy's durable URL (mirrors
                // uploadNewReceipt). The saved FullPath comes from item.path; the blob URL used
                // for the optimistic preview is session-local and dies on reload, so a reopened
                // reconcile couldn't display the receipt. Legacy's URL renders through the
                // <embed>/<iframe> preview (display needs no CORS) and survives reopen; the blob
                // handle stays in _blobUrl for later revocation.
                optimisticAttachment.path = legacyBody.path
                optimisticAttachment.pathWithName = clientDataPath
                optimisticAttachment.mediaFile = mediaFile
                optimisticAttachment.file = mediaFile
                self.emitAttachmentsUpdated()
              }
            } catch (legacyErr) {
              // Surface but don't abort — Platform-side claim still goes through;
              // user will see the receipt in the modal but reopen will hit the
              // empty-path crash. Better to let them save and warn than block.
              console.warn('Legacy upload of folder receipt failed:', legacyErr)
            }
          }
        }

        // PRODUCTDEV-5802 (reopen) — the file transfer is done: lift the spinner
        // and unlock Save BEFORE the prefill round-trip; the coding-row skeleton
        // (scanningReceipt) covers the prefill window, matching the fresh-upload
        // flow's gating (Save unlocks once the upload itself has resolved).
        self.receiptUploadInProgress = false
        self.releaseSaveGate()

        // PRODUCTDEV-5783 — do NOT claim here. The receipt must stay in the folder until
        // the reconcile is actually saved (claimPendingFolderReceipts() runs the deferred
        // FolderSaved → Matched on save success). Prefill still runs against the FolderSaved
        // scan to auto-fill the coding (BuildReceiptPrefill allows FolderSaved).
        await self.tryApplyReceiptScanPrefill(receiptScanId)

        self.$notifications.notify({
          type: 'success',
          message: 'Receipt added from folder',
          placement: { from: 'top', align: 'center' }
        })
      } catch (error) {
        // Roll back the chip + drop the skeleton/spinner and unlock Save —
        // tryApplyReceiptScanPrefill (which clears the skeleton on the happy
        // path) was never reached on this error branch.
        self.rollbackFolderPickAttachment(optimisticAttachment)

        if (error.response && error.response.status === 409) {
          self.$notifications.notify({
            type: 'warning',
            message: 'Receipt state changed — pick another',
            placement: { from: 'top', align: 'center' }
          })
        } else if (error.response && error.response.status === 403) {
          self.$notifications.notify({
            type: 'danger',
            message: 'You no longer have access to this receipt',
            placement: { from: 'top', align: 'center' }
          })
        } else {
          console.error('Failed to add receipt from folder:', error)
          self.$notifications.notify({
            type: 'danger',
            message: 'Failed to add receipt. Please try again.',
            placement: { from: 'top', align: 'center' }
          })
        }
      }
    },

    // PRODUCTDEV-5802 (reopen) — shared rollback for a failed folder pick: remove
    // the optimistic chip, revoke its blob URL, clear the skeleton + spinner, and
    // unlock Save. The picker is already closed at this point (closed on ADD).
    rollbackFolderPickAttachment (attachment) {
      if (attachment) {
        var idx = this.attachedFiles.indexOf(attachment)
        if (idx !== -1) {
          this.attachedFiles.splice(idx, 1)
          this.emitAttachmentsUpdated()
        }
        this.revokeAttachmentBlobUrl(attachment)
      }
      this.scanCompleted = true
      this.scanningReceipt = false
      this.receiptUploadInProgress = false
      this.releaseSaveGate()
    },

    // PRODUCTDEV-5802 (reopen) — counted Save gate. Several uploads can be in
    // flight at once (receipt upload/scan + an Additional Documents upload), and
    // a plain boolean emit lets whichever finishes FIRST unlock Save while the
    // other is still uploading. Acquire emits uploadFinished(false) on first
    // hold; release emits uploadFinished(true) only when every hold is gone.
    acquireSaveGate () {
      this._saveGateCount = (this._saveGateCount || 0) + 1
      this.$emit('uploadFinished', false)
    },

    releaseSaveGate () {
      this._saveGateCount = Math.max(0, (this._saveGateCount || 0) - 1)
      if (this._saveGateCount === 0) {
        this.$emit('uploadFinished', true)
      }
    },

    // ===== PRODUCTDEV-5772 — Additional Documents =====
    // Supporting files attached to the reconcile. Deliberately NOT receipts: never
    // Photon-scanned, never enter attachedFiles/ReceiptFiles, never affect IsComplete
    // or Reconciliation Status. Persisted in their own Legacy table. No progress bar.

    // Plain Legacy byte-upload — no Platform/Photon, no progress bar. The file shows
    // as a chip immediately; it joins the save payload once Legacy returns its path.
    uploadNewDocument: function () {
      var input = document.createElement('input')
      input.type = 'file'
      input.accept = 'application/pdf, image/png, image/jpeg'
      var self = this

      input.onchange = async function (e) {
        var file = e.target.files && e.target.files[0]
        if (!file) {
          return
        }

        // Same allowlist + 10MB cap as the receipt path (ticket: additional docs
        // allow the same file types as the receipt).
        var parts = file.name ? file.name.split('.') : []
        var ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
        var ALLOWED = ['pdf', 'png', 'jpg', 'jpeg']
        if (ALLOWED.indexOf(ext) === -1) {
          self.$notifications.notify({
            type: 'info-warn',
            message: 'Please upload a file with one of these extensions: .pdf, .png, .jpg, .jpeg',
            placement: { from: 'top', align: 'center' }
          })
          return
        }
        if (file.size >= 10 * 1024 * 1024) {
          self.$notifications.notify({
            type: 'info-warn',
            message: 'File is too large. Please upload a document smaller than 10 MB.',
            placement: { from: 'top', align: 'center' }
          })
          return
        }

        var legacyUrl = self.api && self.api.env ? self.api.env + self.api.$.evedpaycard.receipt : null
        if (!legacyUrl) {
          self.showWarningMessage('Failed to upload document. Please try again.')
          return
        }

        var fileType = ext === 'png' ? 'image/png'
          : ext === 'pdf' ? 'application/pdf'
          : ext === 'jpg' ? 'image/jpg'
          : ext === 'jpeg' ? 'image/jpeg'
          : (file.type || '')
        var localObjectUrl = URL.createObjectURL(file)
        // Optimistic chip so the document shows right away. It is NOT part of the
        // save payload until pathWithName lands (emit happens after the upload).
        var doc = {
          name: file.name,
          type: fileType,
          size: file.size,
          file: '',
          mediaFile: '',
          path: localObjectUrl,
          pathWithName: '',
          id: '',
          success: true,
          readonly: false,
          _blobUrl: localObjectUrl
        }
        self.additionalDocuments.push(doc)

        // PRODUCTDEV-5802 (reopen, Case 3) — old-UI spinner over the section and
        // Save gated while the byte-upload is in flight, so a Save click can't
        // race a document that isn't in the payload yet.
        self.documentUploadInProgress = true
        self.acquireSaveGate()
        try {
          var legacyFormData = new FormData()
          legacyFormData.append('attachment_upload', file)
          var legacyToken = self.$user && self.$user.sessionSettings ? self.$user.sessionSettings.Token : ''
          var resp = await self.api.post(legacyUrl, legacyFormData, {
            headers: { 'Authorization': 'Bearer ' + legacyToken, 'Content-Type': 'multipart/form-data' }
          })
          var body = resp && resp.data
          if (typeof body === 'string') {
            try { body = JSON.parse(body) } catch (parseErr) { body = null }
          }
          if (!body || !body.success || !body.path) {
            throw new Error(body && body.uploadError ? body.uploadError : 'Upload failed')
          }
          var match = body.path.match(/(ClientData\/.*)/)
          var clientDataPath = match && match[1] ? match[1] : body.path
          doc.pathWithName = clientDataPath
          doc.mediaFile = body.file || clientDataPath.split('/').pop()
          doc.file = doc.mediaFile
          // PRODUCTDEV-5783 — persist Legacy's durable URL as the path (mirrors uploadNewReceipt).
          // FullPath is saved from doc.path; the blob URL used for the optimistic chip dies on
          // reload, so without this the document can't be opened from a reopened reconcile.
          doc.path = body.path
          self.emitAdditionalDocumentsUpdated()
        } catch (err) {
          console.warn('Document upload failed:', err)
          var idx = self.additionalDocuments.indexOf(doc)
          if (idx !== -1) {
            self.additionalDocuments.splice(idx, 1)
          }
          self.revokeAttachmentBlobUrl(doc)
          self.showWarningMessage('File "' + file.name + '" could not be uploaded. Please try again.')
        } finally {
          self.documentUploadInProgress = false
          self.releaseSaveGate()
        }
      }
      input.click()
    },

    removeDocument (index) {
      var doc = this.additionalDocuments[index]
      if (!doc) return
      this.revokeAttachmentBlobUrl(doc)
      this.additionalDocuments.splice(index, 1)
      this.emitAdditionalDocumentsUpdated()
    },

    openDocument (doc, e) {
      this.openAttachment({ e: e, file: { type: doc.type }, path: doc.path })
    },

    documentIconClass (doc) {
      var name = ((doc && doc.name) || '').toLowerCase()
      if (/\.pdf$/.test(name)) return 'fa-file-pdf'
      if (/\.(png|jpe?g|gif|webp)$/.test(name)) return 'fa-file-image'
      return 'fa-file'
    },

    // Build the Legacy TransactionFile-shaped array and emit ONLY finalized docs
    // (those with a pathWithName). Mirrors emitAttachmentsUpdated; VendorId is
    // required by the FK on EvedPayCardTransactionDocuments.
    emitAdditionalDocumentsUpdated () {
      var vendorId = this.$root.getUser.$.data.VendorID
      var vci = this.$root.getUser.$.data.VCI
      var emitted = this.additionalDocuments
        .filter(item => item && item.pathWithName)
        .map(item => ({
          OriginalName: item.name,
          SizeBytes: item.size,
          MediaFile: item.mediaFile,
          AttachmentFile: item.file,
          PathWithName: item.pathWithName,
          FullPath: item.path,
          ReadOnly: item.readonly || false,
          Id: item.id,
          VendorId: vendorId,
          VCI: vci
        }))
      this.$emit('additional-documents-updated', emitted)
    },

    // Hydrate the document chips from a saved reconcile (ReconcileData.AdditionalDocuments).
    // Emits afterwards so a save that doesn't touch docs re-sends the existing rows
    // (by Id) and SyncReconcileDocuments keeps them instead of soft-deleting.
    populateAdditionalDocuments (documents) {
      if (!Array.isArray(documents)) return
      var self = this
      documents.forEach(function (file) {
        var parts = (file.OriginalName || '').split('.')
        var ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
        var fileType = ext === 'png' ? 'image/png'
          : ext === 'pdf' ? 'application/pdf'
          : ext === 'jpg' ? 'image/jpg'
          : ext === 'jpeg' ? 'image/jpeg'
          : ''
        self.additionalDocuments.push({
          name: file.OriginalName,
          type: fileType,
          size: file.SizeBytes,
          file: file.MediaFile,
          mediaFile: file.MediaFile,
          path: file.FullPath,
          pathWithName: file.PathWithName,
          id: file.Id,
          success: true,
          readonly: self.reconcileAction === 'view'
        })
      })
      this.emitAdditionalDocumentsUpdated()
    },

    uploadNewReceipt: function () {
      var input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*,application/pdf'
      var self = this

      input.onchange = function (e) {
        var file = e.target.files && e.target.files[0]
        if (!file) {
          return
        }

        // <input accept> is a hint only — the OS picker / mobile flows can
        // bypass it. Enforce the same allowlist + 10MB cap as
        // AddReceipt.vue:166-181 and Card-list handleReceiptFileSelected so
        // the BE doesn't have to reject and the user gets useful copy.
        var parts = file.name ? file.name.split('.') : []
        var ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
        var ALLOWED = ['pdf', 'png', 'jpg', 'jpeg']
        if (ALLOWED.indexOf(ext) === -1) {
          self.$notifications.notify({
            type: 'info-warn',
            message: 'Please upload a file with one of these extensions: .pdf, .png, .jpg, .jpeg',
            placement: { from: 'top', align: 'center' }
          })
          return
        }
        var MAX_FILE_SIZE = 10 * 1024 * 1024
        if (file.size >= MAX_FILE_SIZE) {
          self.$notifications.notify({
            type: 'info-warn',
            message: 'File is too large. Please upload a receipt smaller than 10 MB.',
            placement: { from: 'top', align: 'center' }
          })
          return
        }

        // PRODUCTDEV-5475/5788 — "No Receipt Capture" (the division's scanning_disabled mode):
        // attach ANY uploaded receipt as-is via the SAME Legacy upload the scan flow uses, but
        // NEVER call Photon — no scan/prefill/rescan, no overlay/progress. Applies on every
        // surface (receiptCaptureDisabled is self-fetched in created()). The Upload button is
        // already disabled once a receipt is attached (hasAttachment), so this is a first attach.
        if (self.receiptCaptureDisabled) {
          self.attachReceiptWithoutScan(file)
          return
        }

        // PRODUCTDEV-5772 — coding already exists or a receipt is attached: this is a
        // REPLACE, not a first scan. Swap the file without touching Photon or the
        // coding, and surface the "Replace coding?" alert (rescan is opt-in from there).
        if (self.hasCodingOrReceipt) {
          self.replaceReceiptFile(file)
          return
        }

        // Fresh/default reconcile (no coding, no receipt) — the original auto-scan flow.
        self.receiptReplacedPendingRescan = false
        self.pendingRescanFile = null
        self.pendingFolderPrefillScanId = null

        // Gate the parent modal's Save button for the entire upload lifecycle —
        // unlocks only after Platform's scan resolves (or fails), so the user
        // can't save before Photon has had a chance to prefill the form.
        self.acquireSaveGate()

        // Fake 4s progress bar — always runs the full 4 seconds regardless of
        // upload outcome (per Tudor's request). Skeleton AND bar run together:
        // - Bar = progress indicator at the bottom of the upload row.
        // - Skeleton = covers the form's stale Amount + description cells so
        //   the user doesn't see the prior receipt's values bleed through
        //   while the new prefill is still in flight.
        // Cleared by tryApplyReceiptScanPrefill's finally (success or
        // non-retry error) or by the receiptScan.status watcher on Complete
        // / Failed / PlatformUnreachable / PollingTimeout.
        self.scanCompleted = false
        self.scanningReceipt = true
        // Mobile-only full-screen overlay for the whole upload+scan window. Set
        // here (past validation + the replace branch) so the early returns above
        // can't strand it; the scanCompleted watcher clears it on every terminal
        // branch. The template gates the render behind isMobile, so this is inert
        // on desktop. Mirrors BACKUP's placement inside the fresh-scan flow.
        self.uploadingScanOverlay = true
        self.startUploadProgressBar()

        // Optimistic preview — push the file with a local objectURL so the
        // preview pane renders instantly while the uploads run in background.
        var fileType = ext === 'png' ? 'image/png'
          : ext === 'pdf' ? 'application/pdf'
          : ext === 'jpg' ? 'image/jpg'
          : ext === 'jpeg' ? 'image/jpeg'
          : (file.type || '')
        var localObjectUrl = URL.createObjectURL(file)
        var optimisticAttachment = {
          name: file.name,
          type: fileType,
          size: file.size,
          file: '',
          mediaFile: '',
          path: localObjectUrl,
          pathWithName: '',
          id: '',
          success: true,
          readonly: false,
          receiptScanId: null,
          // Track the blob URL separately so removePrimaryAttachment / beforeDestroy
          // can revoke it. `path` is overwritten with Legacy's URL once the upload
          // resolves, so the raw blob handle has to live in its own field.
          _blobUrl: localObjectUrl
        }
        self.attachedFiles.push(optimisticAttachment)
        self.emitAttachmentsUpdated()

        // Dual-upload, Legacy-first (mirrors AddReceipt.vue): Legacy returns the
        // ClientData/... path TransactionFiles.PathWithName needs for reopen-view.
        // Doing it first means pathWithName lands long before Photon finishes, so
        // there's no race with the Save click; sequential reads also avoid the
        // parallel-FormData-read corruption of Photon's PDF multipart body.
        self.uploadReceiptToLegacy(file).then(function (legacy) {
          optimisticAttachment.path = legacy.path
          optimisticAttachment.pathWithName = legacy.pathWithName
          optimisticAttachment.mediaFile = legacy.mediaFile
          optimisticAttachment.file = legacy.file
          self.emitAttachmentsUpdated()
          // Platform — sync Photon /api/v4 extraction for prefill. If this fails the
          // receipt is still saveable (Legacy already has it); the user just doesn't
          // get auto-prefill.
          self.scanReceiptAndPrefill(file, optimisticAttachment)
        }).catch(function (err) {
          // Without the Legacy path the receipt can't be persisted on the
          // transaction's TransactionFiles row, so there's no point continuing.
          console.warn('Legacy receipt upload failed:', err)
          var idxLegacyErr = self.attachedFiles.indexOf(optimisticAttachment)
          if (idxLegacyErr >= 0) {
            self.attachedFiles.splice(idxLegacyErr, 1)
            self.emitAttachmentsUpdated()
          }
          self.revokeAttachmentBlobUrl(optimisticAttachment)
          self.scanCompleted = true
          self.releaseSaveGate()
          self.showWarningMessage('Failed to upload receipt. Please try again.')
        })
      }
      input.click()
    },

    // PRODUCTDEV-5475/5788 — No Receipt Capture (the division's scanning_disabled mode):
    // attach an uploaded receipt to the transaction WITHOUT sending it to Photon. Gated by
    // receiptCaptureDisabled, so it applies on every surface. Mirrors the fresh-upload flow's
    // optimistic attach + Legacy-first upload, but runs no scan, no prefill, no overlay/progress
    // and no rescan prompt; the attachment keeps receiptScanId: null and saves like any
    // non-scanned attachment. Locks Save during the brief Legacy upload (unlocks on
    // resolve/fail). Returns the upload promise (for tests).
    attachReceiptWithoutScan: function (file) {
      var self = this
      // Clear any stale replace/rescan state — scanning never runs in this mode.
      self.receiptReplacedPendingRescan = false
      self.pendingRescanFile = null
      self.pendingFolderPrefillScanId = null

      // Lock Save for the brief Legacy upload window; unlock once the receipt is attached.
      self.acquireSaveGate()

      var parts = file.name ? file.name.split('.') : []
      var ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
      var fileType = ext === 'png' ? 'image/png'
        : ext === 'pdf' ? 'application/pdf'
        : ext === 'jpg' ? 'image/jpg'
        : ext === 'jpeg' ? 'image/jpeg'
        : (file.type || '')
      var localObjectUrl = URL.createObjectURL(file)
      var optimisticAttachment = {
        name: file.name,
        type: fileType,
        size: file.size,
        file: '',
        mediaFile: '',
        path: localObjectUrl,
        pathWithName: '',
        id: '',
        success: true,
        readonly: false,
        receiptScanId: null,
        _blobUrl: localObjectUrl
      }
      self.attachedFiles.push(optimisticAttachment)
      self.emitAttachmentsUpdated()

      // PRODUCTDEV-5802 — old-UI spinner for the upload window; No-Capture mode
      // never shows the 4s bar (no scan), so it previously showed nothing.
      self.receiptUploadInProgress = true
      return self.uploadReceiptToLegacy(file).then(function (legacy) {
        optimisticAttachment.path = legacy.path
        optimisticAttachment.pathWithName = legacy.pathWithName
        optimisticAttachment.mediaFile = legacy.mediaFile
        optimisticAttachment.file = legacy.file
        self.emitAttachmentsUpdated()
        self.receiptUploadInProgress = false
        // No Photon scan to wait on — unlock Save now (receiptScanId stays null).
        self.releaseSaveGate()
      }).catch(function (err) {
        console.warn('Legacy receipt upload failed:', err)
        var idx = self.attachedFiles.indexOf(optimisticAttachment)
        if (idx >= 0) {
          self.attachedFiles.splice(idx, 1)
          self.emitAttachmentsUpdated()
        }
        self.revokeAttachmentBlobUrl(optimisticAttachment)
        self.receiptUploadInProgress = false
        self.releaseSaveGate()
        self.showWarningMessage('Failed to upload receipt. Please try again.')
      })
    },

    // Posts the raw receipt file to the Legacy evednetwork upload endpoint and
    // resolves with the canonical ClientData/... path fields (the values the save
    // flow + reopen-view consume). Shared by first-run upload, Upload-New-Receipt
    // replacement, and folder replacement. Rejects on any non-success response.
    async uploadReceiptToLegacy (file) {
      var legacyUrl = this.api && this.api.env ? this.api.env + this.api.$.evedpaycard.receipt : null
      if (!legacyUrl) {
        throw new Error('No Legacy upload URL configured')
      }
      var legacyFormData = new FormData()
      legacyFormData.append('attachment_upload', file)
      var legacyToken = this.$user && this.$user.sessionSettings ? this.$user.sessionSettings.Token : ''
      var legacyResp = await this.api.post(legacyUrl, legacyFormData, {
        headers: { 'Authorization': 'Bearer ' + legacyToken, 'Content-Type': 'multipart/form-data' }
      })
      var legacyBody = legacyResp && legacyResp.data
      if (typeof legacyBody === 'string') {
        try { legacyBody = JSON.parse(legacyBody) } catch (e) { legacyBody = null }
      }
      if (!legacyBody || !legacyBody.success || !legacyBody.path) {
        throw new Error('Legacy upload rejected the file')
      }
      var match = legacyBody.path.match(/(ClientData\/.*)/)
      var clientDataPath = match && match[1] ? match[1] : legacyBody.path
      var mediaFile = legacyBody.file || clientDataPath.split('/').pop()
      return { path: legacyBody.path, pathWithName: clientDataPath, mediaFile: mediaFile, file: mediaFile }
    },

    // Platform half of the receipt flow, shared by the first-run upload and the
    // Rescan Receipt action. Submits the file to Photon (/api/v4), then starts the
    // scan-status watcher + applies OCR prefill. The receipt is already persisted to
    // Legacy by the caller, so any Platform failure here is non-fatal — the user can
    // still save and fill the coding manually.
    async scanReceiptAndPrefill (file, attachment) {
      var self = this
      var formData = new FormData()
      formData.append('file', file)
      var idempotencyKey = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = (Math.random() * 16) | 0
          var v = c === 'x' ? r : (r & 0x3) | 0x8
          return v.toString(16)
        })
      var extra = {
        uploadContextLegacyTransactionId: self.transaction.data.Id,
        uploadSurface: 'reconcile'
      }
      self.$notifications.notify({
        type: 'info',
        message: 'Scanning receipt...',
        placement: { from: 'top', align: 'center' }
      })
      try {
        var data = await receiptScansApi.submitReceipt(formData, idempotencyKey, extra)
        if (!data || !data.receiptScanId) {
          self.scanCompleted = true
          self.releaseSaveGate()
          self.showWarningMessage('Receipt uploaded but scanning is unavailable. You can fill the form manually.')
          return
        }
        if (attachment) {
          attachment.receiptScanId = data.receiptScanId
        }
        self.releaseSaveGate()
        // Keep the autoLink watcher (584-600) live for the auto-redirect case AND
        // apply OCR prefill unconditionally so the form fills regardless of autoLink.
        // tryApplyReceiptScanPrefill handles all modes (total_only / subtotal_and_tax
        // / line_items_and_tax).
        self.startReceiptScan(data.receiptScanId, { transactionId: self.transaction.data.Id })
        self.tryApplyReceiptScanPrefill(data.receiptScanId)
      } catch (error) {
        // Legacy already succeeded → receipt is saveable + viewable. Unlock save and
        // let the user fill the form manually. Sentinel errors are Platform's
        // deliberate deflection (e.g. flag off), so they're not surfaced as a warning.
        self.scanCompleted = true
        self.releaseSaveGate()
        // PRODUCTDEV-5851 — a 422 means the image couldn't be scanned (e.g. dimensions out of
        // range), but the receipt is still persisted + attached via Legacy. Drop the scanning
        // skeleton (the upload bar engaged it expecting Photon to land, which never happens here)
        // and proceed silently to manual entry — empty fields, no prefill, no warning. This is an
        // expected case, not a failure. Isolated to this status; every other error keeps its handling.
        var imageRejected = error.response && error.response.status === 422
        if (imageRejected) {
          self.scanningReceipt = false
        } else if (!error.isPlatformSentinelError) {
          self.showWarningMessage('Receipt scanning failed. You can fill the form manually.')
        }
      }
    },

    // PRODUCTDEV-5772 — replace an existing receipt via UPLOAD NEW RECEIPT WITHOUT
    // scanning. Uploads the new file to Legacy (so it persists + reopens), swaps the
    // chip, stashes the File for an optional Rescan, and raises the "Replace coding?"
    // alert. The coding is left untouched until the user explicitly clicks Rescan.
    async replaceReceiptFile (file) {
      var self = this
      // PRODUCTDEV-5802 (reopen) — mirror the old ed-upload UX: the FILE shows
      // immediately (optimistic chip) with the old-UI spinner over the section,
      // and Save is gated for the whole upload window so a click can't race the
      // in-flight swap. The chip rolls back if Legacy rejects the upload.
      // Drop the receipt being replaced (and its Platform scan, if any) up front
      // so the new chip is what's visible under the spinner.
      var old = self.primaryAttachment
      if (old) {
        // PRODUCTDEV-5783 — only soft-delete a CLAIMED scan. An unclaimed folder pick
        // (pendingFolderClaim) must stay in the folder, exactly like the ✕ removal does.
        if (old.receiptScanId && !old.pendingFolderClaim) {
          receiptScansApi.deleteReceipt(old.receiptScanId).catch(function (e) {
            console.warn('Soft-delete of replaced ReceiptScan failed (UI continues):', e)
          })
        }
        self.revokeAttachmentBlobUrl(old)
        var oldIdx = self.attachedFiles.indexOf(old)
        if (oldIdx >= 0) self.attachedFiles.splice(oldIdx, 1)
      }
      var parts = file.name ? file.name.split('.') : []
      var ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
      var fileType = ext === 'png' ? 'image/png'
        : ext === 'pdf' ? 'application/pdf'
        : ext === 'jpg' ? 'image/jpg'
        : ext === 'jpeg' ? 'image/jpeg'
        : (file.type || '')
      var blobUrl = URL.createObjectURL(file)
      var optimisticAttachment = {
        name: file.name,
        type: fileType,
        size: file.size,
        file: '',
        mediaFile: '',
        path: blobUrl,
        pathWithName: '',
        id: '',
        success: true,
        readonly: false,
        // No scan yet — replacement is scan-on-demand via Rescan Receipt.
        receiptScanId: null,
        _blobUrl: blobUrl
      }
      self.attachedFiles.push(optimisticAttachment)
      self.emitAttachmentsUpdated()

      self.receiptUploadInProgress = true
      self.acquireSaveGate()
      var legacy
      try {
        legacy = await self.uploadReceiptToLegacy(file)
      } catch (err) {
        console.warn('Legacy upload of replacement receipt failed:', err)
        // Roll back the optimistic chip — the file never landed anywhere.
        var failIdx = self.attachedFiles.indexOf(optimisticAttachment)
        if (failIdx >= 0) self.attachedFiles.splice(failIdx, 1)
        self.revokeAttachmentBlobUrl(optimisticAttachment)
        self.emitAttachmentsUpdated()
        self.receiptUploadInProgress = false
        self.releaseSaveGate()
        self.showWarningMessage('Failed to upload receipt. Please try again.')
        return
      }
      optimisticAttachment.file = legacy.file
      optimisticAttachment.mediaFile = legacy.mediaFile
      optimisticAttachment.pathWithName = legacy.pathWithName
      self.emitAttachmentsUpdated()
      self.receiptUploadInProgress = false
      self.releaseSaveGate()
      self.pendingRescanFile = file
      self.pendingFolderPrefillScanId = null
      // PRODUCTDEV-5843 — no success toast on replace: the inline "Replace coding?"
      // alert below the receipt (driven by receiptReplacedPendingRescan) already
      // conveys this, and the toast was misleading when coding is locked.
      self.receiptReplacedPendingRescan = true
    },

    // PRODUCTDEV-5772 — the user replaced the receipt and chose to update the coding
    // from it. Runs the same Photon scan + prefill the first upload does, using the
    // stashed replacement File, then clears the "Replace coding?" alert.
    rescanReceipt: function () {
      var self = this
      // PRODUCTDEV-5783 — folder replace: the picked receipt already has scan data, so apply its
      // EXISTING prefill (replace the coding) instead of re-running Photon. tryApplyReceiptScanPrefill
      // fills the coding from the FolderSaved scan and clears the skeleton.
      if (self.pendingFolderPrefillScanId) {
        var folderScanId = self.pendingFolderPrefillScanId
        self.pendingFolderPrefillScanId = null
        self.receiptReplacedPendingRescan = false
        self.scanCompleted = false
        self.scanningReceipt = true
        self.tryApplyReceiptScanPrefill(folderScanId)
        return
      }
      if (!self.pendingRescanFile) {
        self.receiptReplacedPendingRescan = false
        return
      }
      var file = self.pendingRescanFile
      self.receiptReplacedPendingRescan = false
      self.pendingRescanFile = null
      // PRODUCTDEV-5783 — gate Save for the scan window so the user can't save mid-scan (matches
      // the first-scan path). Photon re-enables it via uploadFinished(true) when the scan resolves
      // in scanReceiptAndPrefill. Show the progress bar/skeleton for the scan window.
      self.acquireSaveGate()
      self.scanCompleted = false
      self.scanningReceipt = true
      self.startUploadProgressBar()
      self.scanReceiptAndPrefill(file, self.primaryAttachment)
    },

    // PRODUCTDEV-5772 — replace the current receipt with one picked from the folder,
    // WITHOUT scanning. Fetches the folder receipt's bytes, re-uploads to Legacy for a
    // persisted/reopenable path, swaps the chip, stashes the File for an optional
    // Rescan, and raises the "Replace coding?" alert. No claim, no prefill.
    // PRODUCTDEV-5783 — pull the durable ClientData/... S3 key out of a folder receipt's presigned
    // URL. On reopen Legacy rebuilds the image URL as AWS_ASSETS_PRIVATE_PATH + PathWithName via its
    // session-authed Storage/private proxy, so this key (the bytes already sit in the private bucket
    // under .../EvedpayCardTransactionFiles/staged/<guid>/) makes a folder receipt reopen like a
    // normal upload — no browser→S3 byte transfer, no CORS dependency. Decode so the stored key
    // matches the raw key Legacy expects (the URL path is percent-encoded).
    stagedKeyFromPresignedUrl: function (presignedUrl) {
      if (!presignedUrl || typeof presignedUrl !== 'string') return ''
      var m = presignedUrl.match(/(ClientData\/[^?]+)/)
      if (!m || !m[1]) return ''
      try {
        return decodeURIComponent(m[1])
      } catch (e) {
        return m[1]
      }
    },

    async replaceReceiptFromFolder (event) {
      var receiptScanId = event && event.receiptScanId
      if (!receiptScanId || this.folderPickerIsProcessing) {
        return
      }
      this.folderPickerIsProcessing = true
      var self = this
      // PRODUCTDEV-5802 (reopen) — same affordance as Case 1: close the picker on
      // ADD, swap the chip in immediately (file visible from the first frame),
      // old-UI spinner over the Receipt section, Save gated for the whole window.
      this.closeFolderPicker()
      this.receiptUploadInProgress = true
      this.acquireSaveGate()
      // Drop the receipt being replaced (and its Platform scan, if any) up front
      // so the incoming chip is what's visible under the spinner.
      var old = self.primaryAttachment
      if (old) {
        // PRODUCTDEV-5783 — only soft-delete a CLAIMED scan. An unclaimed folder pick
        // (pendingFolderClaim) must stay in the folder, exactly like the ✕ removal does.
        if (old.receiptScanId && !old.pendingFolderClaim) {
          receiptScansApi.deleteReceipt(old.receiptScanId).catch(function (e) {
            console.warn('Soft-delete of replaced ReceiptScan failed (UI continues):', e)
          })
        }
        self.revokeAttachmentBlobUrl(old)
        var oldIdx = self.attachedFiles.indexOf(old)
        if (oldIdx >= 0) self.attachedFiles.splice(oldIdx, 1)
      }
      // Optimistic chip from the folder row's own metadata (authoritative and
      // present even when the S3 byte-fetch below is CORS-blocked; getImage
      // never returns size). Enriched below as each piece resolves.
      var newAttachment = {
        name: (event && event.originalFileName) || 'Receipt',
        type: (event && event.contentType) || '',
        size: (event && event.sizeBytes != null) ? event.sizeBytes : 0,
        file: '',
        mediaFile: '',
        path: '',
        pathWithName: '',
        id: '',
        success: true,
        readonly: false,
        // PRODUCTDEV-5783 — keep the folder scan's association so "Replace coding" can apply its
        // EXISTING prefill, and so it's claimed out of the folder on save (deferred, like a fresh pick).
        receiptScanId: receiptScanId,
        pendingFolderClaim: true,
        _blobUrl: ''
      }
      self.attachedFiles.push(newAttachment)
      self.emitAttachmentsUpdated()
      try {
        var imgRes
        try {
          imgRes = await receiptScansApi.getImage(receiptScanId)
        } catch (imgErr) {
          console.warn('Could not fetch folder receipt image:', imgErr)
        }
        var presignedUrl = imgRes && imgRes.presignedUrl
        var fileName = (event && event.originalFileName) || (imgRes && imgRes.fileName) || 'Receipt'
        var contentType = (event && event.contentType) || (imgRes && imgRes.contentType) || ''
        if (!presignedUrl) {
          self.rollbackFolderPickAttachment(newAttachment)
          self.$notifications.notify({ type: 'warning', message: 'Receipt is no longer in the folder — pick another', placement: { from: 'top', align: 'center' } })
          return
        }
        // PRODUCTDEV-5783 — durable reopen WITHOUT any S3 byte transfer. Legacy serves receipts on
        // reopen as AWS_ASSETS_PRIVATE_PATH + PathWithName (a session-authed Storage/private proxy
        // over the private bucket). The folder receipt's bytes already live in that bucket under
        // .../EvedpayCardTransactionFiles/staged/<guid>/, so storing that key as PathWithName makes
        // the receipt reopen exactly like a normal upload — no fetch, no CORS, no re-upload.
        var stagedKey = self.stagedKeyFromPresignedUrl(presignedUrl)
        // Still fetch the bytes BEST-EFFORT: when it succeeds we ALSO push a permanent (non-staged)
        // copy to Legacy and prefer that PathWithName. When CORS blocks it (e.g. localhost) `blob`
        // stays null, we skip the upload, and the staged key above keeps reopen working.
        var blob = null
        var blobUrl = ''
        try {
          var imgFetch = await fetch(presignedUrl)
          if (imgFetch && imgFetch.ok) {
            blob = await imgFetch.blob()
            blobUrl = URL.createObjectURL(blob)
            contentType = blob.type || contentType
          }
        } catch (fetchErr) {
          console.warn('Could not fetch folder receipt bytes (CORS?) — continuing without Legacy upload:', fetchErr)
        }
        // Real size from the folder row; fall back to the fetched blob only if the row didn't carry one.
        var fileSize = (event && event.sizeBytes != null) ? event.sizeBytes : (blob ? blob.size : 0)

        // Enrich the early chip — preview via the local blob URL when the fetch
        // succeeded, else the presigned S3 URL (cross-origin <img> needs no CORS).
        // pathWithName defaults to the staged key so reopen works; a successful
        // Legacy upload below overwrites it with the permanent path.
        newAttachment.name = fileName
        newAttachment.type = contentType
        newAttachment.size = fileSize
        newAttachment.path = blobUrl || presignedUrl || ''
        newAttachment.pathWithName = stagedKey
        newAttachment._blobUrl = blobUrl || ''
        self.emitAttachmentsUpdated()

        // Legacy dual-upload — ONLY when we actually have the bytes. Overwrites path/pathWithName
        // with Legacy's durable URL so the receipt survives reopen (mirrors uploadNewReceipt /
        // the fresh folder-pick at :1900). Non-fatal on failure: the receipt is still attached and
        // saveable, the Platform claim still goes through; reopen just falls back to the scan image.
        if (blob) {
          try {
            var blobType = blob.type || contentType || 'application/octet-stream'
            var legacyFile = new File([blob], fileName, { type: blobType })
            var legacy = await self.uploadReceiptToLegacy(legacyFile)
            newAttachment.path = legacy.path
            newAttachment.pathWithName = legacy.pathWithName
            newAttachment.mediaFile = legacy.mediaFile
            newAttachment.file = legacy.file
            self.emitAttachmentsUpdated()
          } catch (legErr) {
            console.warn('Legacy upload of replacement folder receipt failed (UI continues):', legErr)
          }
        }

        // PRODUCTDEV-5802 (reopen) — transfer done: lift the spinner, unlock Save.
        self.receiptUploadInProgress = false
        self.releaseSaveGate()

        // The folder receipt already has scan data: offer to replace the coding WITH it (no re-scan).
        // pendingRescanFile stays null so rescanReceipt applies the folder prefill instead of Photon.
        self.pendingRescanFile = null
        self.pendingFolderPrefillScanId = receiptScanId
        // PRODUCTDEV-5843 — no success toast on replace: the inline "Replace coding?"
        // alert below the receipt already conveys this (see replaceReceiptFile).
        self.receiptReplacedPendingRescan = true
        self.closeFolderPicker()
      } catch (error) {
        console.error('Failed to replace receipt from folder:', error)
        self.rollbackFolderPickAttachment(newAttachment)
        self.$notifications.notify({ type: 'danger', message: 'Failed to add receipt. Please try again.', placement: { from: 'top', align: 'center' } })
      }
    },

    startUploadProgressBar: function () {
      this.cancelUploadProgressBar()
      this.uploadingReceipt = true
      this.uploadProgressPct = 0
      var self = this
      var start = performance.now()
      var duration = 4000
      var tick = function (now) {
        var pct = Math.min(100, Math.round(((now - start) / duration) * 100))
        self.uploadProgressPct = pct
        if (pct < 100) {
          self._uploadProgressRaf = requestAnimationFrame(tick)
        } else {
          self._uploadProgressRaf = null
          self.uploadingReceipt = false
          // Bar finished. If Photon hasn't responded yet, flip to skeleton state;
          // otherwise the prefill is already on the form so we render nothing.
          if (!self.scanCompleted) {
            self.scanningReceipt = true
          }
        }
      }
      this._uploadProgressRaf = requestAnimationFrame(tick)
    },

    cancelUploadProgressBar: function () {
      if (this._uploadProgressRaf) {
        cancelAnimationFrame(this._uploadProgressRaf)
        this._uploadProgressRaf = null
      }
      this.uploadingReceipt = false
      this.uploadProgressPct = 0
    },

    formatBytes (bytes, decimals) {
      if (bytes == null || bytes === 0) return '0 Bytes'
      var k = 1024
      var dm = decimals != null ? decimals : 2
      var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      var i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
    },

    openPrimaryAttachment (e) {
      var f = this.primaryAttachment
      if (!f) return
      var name = (f.name || f.file || '').toLowerCase()
      var type = f.type || (/\.pdf$/.test(name) ? 'application/pdf'
        : /\.png$/.test(name) ? 'image/png'
        : /\.jpe?g$/.test(name) ? 'image/jpeg'
        : '')
      this.openAttachment({ e: e || { preventDefault: function () {} }, file: { type: type }, path: f.path })
    },

    removePrimaryAttachment () {
      var f = this.primaryAttachment
      if (!f) return
      // Soft-delete on Platform if this is a Platform-uploaded scan. Fire-and-forget so
      // UI removal always succeeds; the DELETE handler is idempotent on already-deleted
      // rows and tolerates any non-Submitted status (DeleteFolderReceiptCommandHandler).
      // PRODUCTDEV-5783 — a folder receipt picked this session but not yet claimed
      // (pendingFolderClaim) must NOT be deleted on removal: it still lives in the folder,
      // so removing it from this reconcile should simply leave it there for later use.
      if (f.receiptScanId && !f.pendingFolderClaim) {
        receiptScansApi.deleteReceipt(f.receiptScanId).catch(function (err) {
          console.warn('Soft-delete of ReceiptScan failed (UI continues):', err)
        })
      }
      this.revokeAttachmentBlobUrl(f)
      var idx = this.attachedFiles.indexOf(f)
      if (idx >= 0) {
        this.attachedFiles.splice(idx, 1)
      }
      // Reset scan METADATA only so a later Rescan's prefill can apply cleanly.
      // This intentionally does NOT touch the coding values themselves.
      this.receiptScanPrefillApplied = false
      this.receiptScanPrefillMode = null
      this.receiptScanConfidence = null
      this.amountMismatch = false
      this.receiptScanPhotonTotal = null
      // PRODUCTDEV-5772 — removing the receipt clears any pending "Replace coding?" alert.
      this.receiptReplacedPendingRescan = false
      this.pendingRescanFile = null
      this.pendingFolderPrefillScanId = null

      // PRODUCTDEV-5772 — removing the receipt must NOT wipe the coding. Previously
      // this collapsed the form to one row and cleared Amount/descriptions; now the
      // already-entered/scanned coded items persist (the user can still edit them).
      // Because coding remains, the next new-receipt upload sees it via
      // hasCodingOrReceipt and offers Rescan instead of auto-scanning.
      this.emitAttachmentsUpdated()
    },

    // Build the Card-list-shaped attachment array from this.attachedFiles and emit it.
    // Card-list listens on @attachmentsUpdated → updateAttachmentsFiles → this.filteredAttachments,
    // which feeds createReconcile's IsComplete validation + the ReceiptFiles payload.
    // Without this emit, uploads + folder picks never reach Card-list's state, so
    // filteredAttachments stays empty and saves come back with ReceiptFiles: [] and
    // IsComplete: false (status stays Open instead of moving to Coded).
    //
    // VendorId + VCI MUST be included — Legacy's EvedPayCardTransactionFiles table
    // has FK_EvedPayCardTransactionFiles_Vendors on VendorID. Without these the
    // INSERT violates the constraint and the reconcile POST returns 500.
    emitAttachmentsUpdated () {
      var vendorId = this.$root.getUser.$.data.VendorID
      var vci = this.$root.getUser.$.data.VCI
      var emitted = this.attachedFiles.map(item => ({
        OriginalName: item.name,
        SizeBytes: item.size,
        MediaFile: item.mediaFile,
        AttachmentFile: item.file,
        PathWithName: item.pathWithName,
        FullPath: item.path,
        ReadOnly: item.readonly || false,
        Id: item.id,
        VendorId: vendorId,
        VCI: vci
      }))
      this.$emit('attachmentsUpdated', emitted)
    },

    // Called by the parent's closeReconcile() (Card-transactions/index.vue) when the user
    // dismisses the modal without saving. Soft-deletes every Platform receipt-scan that
    // was uploaded in this session so it doesn't linger as an orphan with OCR data.
    cleanupUnsavedReceipts () {
      if (!Array.isArray(this.attachedFiles) || this.attachedFiles.length === 0) return
      this.attachedFiles.forEach(function (f) {
        // Only dispose scans uploaded INSIDE this reconcile session (UPLOAD NEW
        // RECEIPT button). Two flags are deliberately left alone on cancel:
        //  - preExistingMatch: scans from a prior Dashboard-SUBMIT match — already Matched,
        //    the scan stays linked at the Platform level for a later visit.
        //  - pendingFolderClaim: folder receipts picked this session but NOT yet claimed
        //    (PRODUCTDEV-5783) — they must stay FolderSaved so they remain in the folder
        //    when the user cancels instead of saving.
        if (f && f.receiptScanId && !f.preExistingMatch && !f.pendingFolderClaim) {
          receiptScansApi.deleteReceipt(f.receiptScanId).catch(function (err) {
            console.warn('Soft-delete of unsaved ReceiptScan failed:', err)
          })
        }
      })
    },

    // PRODUCTDEV-5783 — called by the host on reconcile SAVE success. Performs the deferred
    // FolderSaved → Matched claim for any receipt picked from the folder this session, so a
    // folder receipt only leaves the folder once the reconcile is actually saved (never on
    // cancel). Re-fetches the rowVersion first (prefill bumped it after the pick) and skips
    // anything no longer FolderSaved. Best-effort + fire-and-forget — a failed claim just
    // leaves the scan in the folder rather than blocking the save that already succeeded.
    claimPendingFolderReceipts () {
      if (!Array.isArray(this.attachedFiles) || this.attachedFiles.length === 0) return
      var legacyTxId = this.transaction && this.transaction.data ? this.transaction.data.Id : null
      if (!legacyTxId) return
      this.attachedFiles.forEach(function (f) {
        if (!f || !f.receiptScanId || !f.pendingFolderClaim) return
        // Clear the flag up front so a re-entrant save can't double-claim the same scan.
        f.pendingFolderClaim = false
        var scanId = f.receiptScanId
        receiptScansApi.getStatus(scanId)
          .then(function (statusData) {
            if (!statusData || statusData.status !== 'FolderSaved') return null
            return receiptScansApi.claimReceipt(scanId, legacyTxId, statusData.rowVersion)
          })
          .catch(function (err) {
            console.warn('Deferred folder-receipt claim failed (receipt stays in folder):', err)
          })
      })
    },

    revokeAttachmentBlobUrl (attachment) {
      if (!attachment || !attachment._blobUrl) return
      // Guard the scheme — `path` can be swapped to Legacy's https URL after
      // upload, but `_blobUrl` holds the original handle. Only call revoke on
      // blob: URLs (no-op otherwise, but cheap to check defensively).
      if (typeof attachment._blobUrl === 'string' && attachment._blobUrl.indexOf('blob:') === 0) {
        URL.revokeObjectURL(attachment._blobUrl)
      }
      attachment._blobUrl = ''
    }
  },

  beforeDestroy () {
    // Revoke any blob URLs still pinned on attachments. Splice paths
    // (removePrimaryAttachment, upload-failure branches) already revoke
    // individually; this catches the happy-path teardown where the user
    // saves and the modal closes with attachments still in the array.
    var self = this
    if (Array.isArray(this.attachedFiles)) {
      this.attachedFiles.forEach(function (f) {
        self.revokeAttachmentBlobUrl(f)
      })
    }
    // PRODUCTDEV-5772 — also revoke any object URLs held by additional documents.
    if (Array.isArray(this.additionalDocuments)) {
      this.additionalDocuments.forEach(function (d) {
        self.revokeAttachmentBlobUrl(d)
      })
    }
  }
}
</script>

<style lang='scss' scoped>
@import "~@/assets/sass/_variables.scss";

.borderless >>> {
  input.form-control {
    border: none;
    // box-sizing: border-box keeps the input inside the td's content area.
    // With content-box (Bootstrap default), width:100% + padding overflows
    // the cell and stacks on top of the cell's right border, which is what
    // made the Category / Category Ref ID pipe visually disappear after a
    // category was selected (focus moved + box-shadow shifted the stacking).
    box-sizing: border-box;
  }
  .dawnSelect {
    // .dawnSelect declares box-sizing: content-box + width: 100% + a 1px
    // left/right border (from the form-field mixin). The previous rule only
    // removed top/bottom — left/right remained and made the element 2px
    // wider than its cell, overflowing onto the next cell's border and
    // hiding the visual pipe between Category and Category Ref ID.
    border: none;
    box-sizing: border-box;
  }
  .dawnSelect__content-wrapper {
    min-width: 340px;
  }
}
.money-input {
  margin: 0;
  &, >>> input {
    height: 24px;
    line-height: 24px;
    padding-top: 0;
    padding-bottom: 0;
  }
}
.split-button {
  position: relative;
  left: -5px;
}

.reconcile-transaction-overlay {
  background-color: white;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.full-width-table {
  width: 100%;
  margin: 0px;
  padding: 0;
}

.full-width-table th {
  border: none;
  /* Coding column headers — match the Figma label style (Source Sans Pro
     SemiBold 13px #333) and keep all columns visually consistent. Without
     this, plain <th> cells inherit the browser's default bold ~16px while
     the .description-col / .amount-col overrides shrink to 13-13.6px,
     producing three different sizes in one row. */
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  text-align: left;
  padding: 4px 8px;
  line-height: 16px;
}

.full-width-table td {
  padding: 0px !important;
  border: 1px solid #ccc; 
  margin: 0px !important;
  font-size: 15px;
}

.overview .overview-card {
  width: 100%;
  min-height: 0;
  margin: 0;
  padding: 0;
  padding-bottom: 10px;
}

.overflow-merchant {
  text-overflow: ellipsis;
  overflow: hidden;
}

.optional-text {
  font-style: italic;
  opacity: 0.6;
}

.description-field {
  .description-textarea {
    display: block;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    resize: none;
  }
}

.btn-custom {
  background-color: white !important;
  color: blue;
  border: 2px solid blue;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 15px;
  cursor: pointer;
}

.btn-custom:hover {
  background-color: white;
  color: blue;
}

// List entry
.option {
  &-combo-img {
    width: 48px;
    margin: 5px 10px 5px 2px;
    display: block;
    float: left;
  }
  &-bold {
    display: inline-block;
    width: 85%;
    max-width: 87%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: bold;
  }
  &-em {
    font-weight: $font-weight-bold;
    float: right;
    @include theme {
    color: v(primary-05);
    }
  }
  &-strong {
    display: inline-block;
    width: 85%;
    max-width: 87%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @include theme {
    color: v(primary-05);
    }
  }
}

// Card entry
.supplier {
  &-txt {
    width: 60%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
  &-strong {
    max-width: 80%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    @include theme {
    color: v(primary-05);
    }
  }
  &-em {
    font-size: 15px;
    font-weight: $font-weight-bold;

    margin-top: 3px;
    display: inline-block;
    position: absolute;
    right: 40px;

    @include theme {
    color: v(primary-05);
    }
  }
  &-details[class] {
    width: 100%;
    height: 190px;

    .status-label {
      margin-top: 10px;
    }
  }
  &-img {
    height: 90px;
    width: 90px;
    margin: 5px 10px 5px 2px;
    display: block;
    float: left;
  }
  &-evedpay{
    @include theme {
    color: v(canvas);
    background-color: #009FCC;
    border-color: #009FCC;
    }
  }
  &-not-evedpay{
    @include theme {
    color: v(canvas);
    background-color: #e02630;
    border-color: #e02630;
    }
  } 
  &-type-preferred {
    @include theme {
    color: v(canvas);
    background-color: #009FCC;
    border-color: #009FCC;
    }
  }
  &-type-approved {
    @include theme {
    color: v(canvas);
    background-color: #75A628;
    border-color: #75A628;
    }
  }
  &-type-pending {
    color: #666666;
    background-color: #FFFFFF;
    border-color: #666666;
  }
  &-default-status {
    color: #666666;
    background-color: #FFFFFF;
    border-color: #666666;
  }
}

td input.custom-fields::placeholder {
  color: white;
}

.custom-dropdown-supplier >>> input[disabled]::placeholder {
  color: #333333;
}

.label {
  text-transform: capitalize;
}
.supplier-txt .label {
  height: 20px;
}

.contact-icon {
  @include theme {
    color: v(tertiary-01);
  }
}
.close-attachment-button {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 100;
  cursor: pointer;
  color: #000 !important;
  display: none;
  font-size: 1.5em;
}
.transaction-attachment.open + .close-attachment-button {
  display: block;
}

.transaction-attachment.open + .close-attachment-button {
  display: block;
}
.transaction-attachment {
  border: 0;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100vh);
  display: none;
  z-index: 99;
  background: #fff;

  &.open {
    display: block;
  }
}

.overview-card >>> .dawn-card-footer {
  display: none;
}

a.row-action {
  text-decoration: underline;
}

/* ----------------------------------------------------------------------------
 * Coding table — general layout rules.
 *
 * Goals:
 *   - Every row is the same compact height regardless of which cells the row
 *     contains (Description text vs ed-select vs ed-input vs v-money) or how
 *     many custom-field columns the client config produces.
 *   - All headers + cells visually align in a clean grid.
 *   - The action-links column on the far left doesn't push the data area
 *     unpredictably to the right.
 *
 * Mechanics:
 *   - Compress ed-select's internal min-height from 40px → 32px inside this
 *     table only (left alone everywhere else in the app).
 *   - vertical-align: middle on every cell so content sits centred in the row.
 *   - Fixed widths on the action-links + Amount columns (the only two columns
 *     we want stable). All other columns split the remaining width
 *     proportionally via table-layout: auto.
 * --------------------------------------------------------------------------*/

/* Action-links column — width covers 4 links (+ Split / Copy / Paste / Remove)
   on one line without wrapping. Strip the cell border. */
.full-width-table td.split-buttons,
.full-width-table th.split-buttons {
  border: none !important;
  width: 175px;
  padding: 4px 4px !important;
  white-space: nowrap;
  font-size: 13px;
}

.receipt-file-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  // PRODUCTDEV-5804 — dividers only BETWEEN adjacent chips (the + rule below): no stray line under
  // a lone Receipt; Additional Documents separated with no leading/trailing line.
  margin-top: 8px;
  font-size: 13px;

  > i.fas {
    flex-shrink: 0;
    font-size: 16px;
  }
  // PRODUCTDEV-5804 — restore colored, file-type-specific icons (ed-upload palette) the V2 chip dropped.
  > i.fa-file-pdf   { color: #cb0404; }
  > i.fa-file-image { color: #4598d9; }
  > i.fa-file       { color: #acacac; }
}

.receipt-file-chip + .receipt-file-chip {
  border-top: 1px solid #e0e0e0;
}

.receipt-file-chip-name {
  flex: 1 1 auto;
  min-width: 0;
  color: #4a90e2;
  text-decoration: underline;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.receipt-file-chip-size {
  flex-shrink: 0;
  margin-left: auto;
  color: #888;
  font-size: 12px;
}

.receipt-file-chip-remove {
  flex-shrink: 0;
  color: #888;
  text-decoration: none;
  font-size: 16px;
  line-height: 1;
  margin-left: 4px;

  &:hover {
    color: #333;
  }
}

.receipt-scan-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

// PRODUCTDEV-5804 — consistent breathing room above each form section header.
.receipt-section,
.additional-documents,
.coding-section {
  margin-top: 12px;
}

.receipt-action-button {
  border: 1px solid #ccc;
  background-color: #fff;
  padding: 6px 16px;
  text-transform: none;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18px;
  cursor: pointer;
  border-radius: 3px;
  color: #333;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: #efefef;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

/* PRODUCTDEV-5790 — view-mode receipt buttons are disabled but must still show a hover
   tooltip (placed on the wrapping <span>). A native disabled <button> neither fires nor
   bubbles mouse events, so the span would never see mouseenter; pointer-events:none lets
   the hover fall through to the span. The span then owns the not-allowed cursor — it only
   surfaces while the button is disabled, since an enabled button keeps its own pointer
   events and pointer cursor. Scoped with `> span >` so the unwrapped Additional Documents
   buttons (direct <button> children of .receipt-scan-actions) are unaffected. */
.receipt-scan-actions > span {
  display: inline-flex;
  cursor: not-allowed;
}

.receipt-scan-actions > span > .receipt-action-button:disabled {
  pointer-events: none;
}

.full-width-table th.description-col,
.full-width-table td.description-col {
  width: 157px;
  font-size: 13px;
}

// Description cell is now editable (ed-input with .description-input). The
// previous text-mode padding (6px 8px) and word-break would have created a
// double border / oversized cell next to the other inline-edited columns;
// the input now fills the cell with its own padding, matching how Category
// Ref ID renders.

/* Amount column — let the table cell's native 1px #ccc border (from
   .full-width-table td) provide the box, same as every other coding cell. Give
   the column a fixed width so single-row currency text doesn't shrink the cell
   to a tiny stub, and right-align the value the way currency reads. Header
   font stays governed by .full-width-table th so it matches the other column
   headers; only the column-specific shape (width, alignment) is overridden. */
.full-width-table th.amount-col,
.full-width-table td.amount-col {
  width: 120px;
  text-align: right;
}

.full-width-table td.amount-col {
  padding: 0 4px !important;
  font-size: 13.6px;
  color: #333;
}

.full-width-table td.predicted-category-cell {
  position: relative;
}

.predicted-coding-icon-container {
  height: 16px;
  line-height: 16px;
  pointer-events: none;
  position: absolute;
  right: 44px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  z-index: 20;
}

.predicted-category-cell.has-predicted-coding-icon >>> .dawnSelect__input,
.predicted-category-cell.has-predicted-coding-icon >>> .dawnSelect__single {
  padding-right: 64px;
  text-overflow: ellipsis;
}

.full-width-table td.amount-col .money-input {
  width: 100%;
}

.middle-band {
  display: block;
}

.middle-band:after {
  content: "";
  display: table;
  clear: both;
}

.middle-band-left,
.middle-band-right {
  float: left;
}

/* Receipt preview panel — Figma 1494:4167: plain "Receipt" label sitting above
   a #fafafa / #ececec image container. No bordered card, no header bar. */
.receipt-preview-panel {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
}

.receipt-preview-label {
  display: block;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: #333;
  margin-bottom: 6px;
}

// PRODUCTDEV-5772 — "Replace coding?" alert. Shown between the Receipt buttons and
// Additional Documents after the user replaces an already-attached receipt without
// rescanning. Figma node 1970:15335 (Calendula Gold palette). Offers Rescan Receipt
// as the opt-in to re-run Photon and overwrite the coding.
.replace-coding-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background-color: #FFFBE6;
  border: 1px solid #FFE58F;
  border-radius: 2px;
  padding: 8px 16px;
  margin-top: 8px;
}

.replace-coding-alert-text {
  font-size: 14px;
  line-height: 22px;
  color: rgba(0, 0, 0, 0.85);
}

.replace-coding-alert-action {
  flex: 0 0 auto;
  font-size: 14px;
  line-height: 22px;
  color: #1890ff;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #40a9ff;
  }
}

// Compact warning shown when Photon's receipt total differs from the matched
// card-charge amount. The BE used to downgrade to total_only mode in this case;
// now it preserves the breakdown and emits warning.amount_mismatch. Yellow
// palette, single line, informational — not blocking.
.amount-mismatch-banner {
  // inline-flex so the banner shrinks to its content width instead of
  // stretching the full modal width and leaving empty space on the right.
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #FFF7E0;
  border: 1px solid #FFD666;
  border-radius: 3px;
  padding: 4px 10px;
  margin: 12px 0 6px;
  color: #874D00;
  font-size: 12px;
  line-height: 16px;

  i.fa-exclamation-triangle {
    color: #FA8C16;
    font-size: 12px;
    flex: 0 0 auto;
  }
}

.receipt-upload-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;

  .receipt-upload-progress-track {
    flex: 1;
    height: 8px;
    background: #F5F5F5;
    border-radius: 100px;
    overflow: hidden;
  }

  .receipt-upload-progress-fill {
    height: 100%;
    background: #1890FF;
    border-radius: 100px;
    transition: width 60ms linear;
  }

  .receipt-upload-progress-label {
    font-family: Roboto, sans-serif;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.85);
    width: 32px;
    text-align: right;
  }
}

.skeleton {
  display: inline-block;
  background: linear-gradient(90deg, #F0F0F0 25%, #f7f7f7 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 3px;
}

.skeleton-line {
  height: 14px;
  width: 100%;
}

.skeleton-description {
  width: 120px;
}

.skeleton-amount-cell {
  width: 80px;
}

.coding-row-skeleton-cell {
  padding: 6px 0;
}

.coding-row-skeleton {
  height: 32px;
  width: 100%;
}

/* "Uploading receipt…" full-screen overlay during UPLOAD NEW RECEIPT's
   upload+Photon round-trip. Visual mirror of mobile.vue's .receipt-upload-overlay
   so the user sees the same loading affordance whether the upload was triggered
   from the FAB or from inside the Reconcile screen. z-index sits above the form
   (default 0) but below ed-modal (1000000), so any open modal still takes priority.
   Toggled by the uploadingScanOverlay data flag (uploadNewReceipt at start,
   scanCompleted watcher at every terminal branch). Rendered only when isMobile. */
.receipt-scan-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.receipt-scan-overlay-card {
  width: 80%;
  max-width: 280px;
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem 1.25rem;
  text-align: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.receipt-scan-overlay-spinner {
  font-size: 28px;
  color: #1976d2;
  margin-bottom: 0.75rem;
}

.receipt-scan-overlay-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.receipt-scan-overlay-subtitle {
  font-size: 13px;
  color: #6b6b6b;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

</style>

<style lang="scss">
/* Non-scoped overrides for the legacy <ed-upload> render path inside the
   reconcile modal. Kept out of the scoped block above because Vue's ::v-deep
   compilation wasn't beating the global theme mixin in fileUploadComponent.vue
   (`.file-uploads .btn { @include btn-styles(v(ink-light)); }`). The
   `.receipt-upload-legacy` selector exists only in this file so the global
   leak is bounded to the reconcile modal. */

/* Button: match the .receipt-action-button look (grey border, white bg, dark text). */
.receipt-upload-legacy .file-uploads .btn,
.receipt-upload-legacy .file-uploads .btn.btn-primary,
.receipt-upload-legacy .file-uploads .btn.btn-primary.alt {
  display: inline-block !important;
  border: 1px solid #ccc !important;
  background: #fff !important;
  background-image: none !important;
  padding: 6px 16px !important;
  margin: 0 !important;
  text-transform: uppercase !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.18px !important;
  color: #333 !important;
  border-radius: 3px !important;
  box-shadow: none !important;
  text-shadow: none !important;
  transition: background-color 0.15s ease;

  &:hover,
  &:focus,
  &:active {
    background: #efefef !important;
    color: #333 !important;
    border-color: #ccc !important;
    box-shadow: none !important;
  }

  &.disabled,
  &[disabled] {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
  }
}

/* File chip row: flex layout. Default <ed-upload> uses absolute positioning for
   .dawn-file-size and .dawn-remove-file which causes long filenames to overlap
   and uneven spacing. Inside .receipt-upload-legacy we override to a single
   flex row: icon | name (ellipsised) | size | remove. !important needed
   because the original .dawn-file-* rules in edUpload/index.vue are unscoped
   and set absolute positioning we have to unset. */
.receipt-upload-legacy .dawn-file-list {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  padding: 0.3rem 0.6rem !important;

  > a,
  > input.file-upload-item-input {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    width: auto !important;
    overflow: hidden !important;
    white-space: nowrap !important;
    text-overflow: ellipsis !important;
  }

  > .dawn-file-icon {
    flex: 0 0 auto !important;
    margin-right: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
  }

  > .dawn-file-size {
    position: static !important;
    flex: 0 0 auto !important;
    right: auto !important;
    top: auto !important;
    white-space: nowrap !important;
  }

  > .dawn-remove-file {
    position: static !important;
    flex: 0 0 auto !important;
    right: auto !important;
    top: auto !important;
    line-height: 1 !important;
  }
}

/* Receipt image surface — Figma 1494:4169: #fafafa background with thin #ececec
   border, no radius. Override only inside .receipt-preview-panel (reconcile
   modal) so the matching modal's ReceiptImageViewer keeps its own #f5f5f5
   surface. !important needed because the source rule lives in ReceiptImageViewer's
   scoped block, which adds a data-v attribute and ties on specificity. */
.receipt-preview-panel .receipt-image-wrap {
  background-color: #fafafa !important;
  border: 1px solid #ececec !important;
  border-radius: 0 !important;
}

/* Coding table CSS overrides removed — they were aggressive height resets
   targeting .form-group / .dawnSelect / .money-input / .dawnSelect__tags etc.
   They ended up breaking the multi-row (splitCounter > 1) layout that was
   originally working fine. Reverted to let the original ed-input / ed-select
   render at their natural heights. The only coding-table CSS that remains
   lives in the SCOPED block above (action-links column width, amount-col
   class for the read-only single-row Amount cell, header font on .full-width-table
   th). */

</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-transactions/ReconcileTransaction.vue